/**
 * ZEN ERP — Teklif Oluşturma Sayfası ve İlgili Bileşenler
 * App.js'den çıkarıldı: QuoteBuilderPage, QuoteViewsModal,
 * QuoteRevisionHistoryModal, QuotesListModule.
 */
import React, { useState, useEffect, useContext, useRef, useCallback, useMemo } from "react";
import {
  Plus, X, Save, ChevronDown, ChevronUp, Pencil, Copy, Share2, Send,
  FileText, Trash2, Clock, Eye, RefreshCw, MessageCircle, Download,
  ChevronRight, ExternalLink, Package, Upload, FileSignature,
  FileDown, History, TrendingUp, Building2, CheckCircle2, MapPin, ArrowLeft, Mail, GripVertical, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";

import { UICtx, AuthCtx } from "../appContexts";
import { ModuleIcon, PName } from "../components/layout/Sidebar";
import api from "../api";
import { MONEY, fmt, fmtDate, ono, convertPrice, shareUrl } from "../appUtils";
import { resetDraftAfterAdd, duplicateItem, isDuplicateShortcut, freshHighlightClass, duplicateTargetIndex, isTextEntryTarget, showPreviewRow, scaleItemQty, shouldAutoScroll, showDraftRestoreBanner } from "../utils/fastEntry";
import { isTulFonItem, qtyUnitLabel } from "../utils/quoteUnits";
import { computeQuoteTotals, manualLineTotalFromUnit } from "../utils/quoteTotals.js";
import { validateStorSistemDraft, findPriceItem, RECIPE_CODES, buildStorSistemComponents, applyKumandaRule, replaceStorItem, isKumandaOptOut } from "../utils/storRecipe.js";
import { ProductForm, SparePartPickerModal } from "../components/OrderForm";
import {
  computeLineTotal, applyDealerGroupPricing, applyCustomerPricing, applyGlobalMarginPricing,
  PRODUCT_GROUP_FIELDS, detectGroupSlug, getProductTypeKey, nattyPliseFabricGroupKey,
  METAL_JALUZI_SLUG, DIKEY_RAY_SLUG, STOR_SISTEM_SLUG,
  STOR_MEKANIZMA_PRICES, STOR_MEKANIZMA_COST_USD, STOR_KASA_FARK, STOR_KASA_RENGI_STANDART,
  STOR_KASA_RENGI_FARKI, STOR_ALT_CITA_FARK, STOR_YAN_KANAL_TL_MT,
  STOR_ZIGIL_TL, STOR_ETEK_FARK, STOR_ZINCIR_FARK, STOR_REDUKTOR_TL,
  STOR_YAYLI_MEK_TL, STOR_COCUK_KILIDI_TL, ZEBRA_ETEK_FARK,
  DELUX_ELCEK_TL, LAZER_ETEK_PRICES,
} from "../utils/quoteUtils";
import { applySegmentPricing, computeItemAutoCost, segmentRate, SEGMENT_RATE_MIN } from "../utils/segmentPricing";
import MaliyetKarPanel from "../components/MaliyetKarPanel";
import {
  smartSharePDF, whatsappShare, buildOrderSummaryText,
  fetchEmailTemplate, renderEmailTemplate, logSend, apiErrorMessage,
  buildTenteksSiparisFoyuHTML, printViaIframe,
} from "../utils/pdfUtils";
import { QUOTE_BRANDS, BrandSelectModal, defaultQuoteNotes, patchNotesNakliye, QuotePrintModal, buildTeklifPrintHTML, buildQuoteWhatsappText, WHATSAPP_FIELDS } from "../quotes";
import { lookupSkyZipPrice, lookupArtZipPrice, setArtZipMatrices, setSkyZipMatrices, REMOTE_PRICES } from "../artbeyPricing";
import ShareMapModule from "../shareMap";

// Ürün grubu kategorilendirme — İç/Dış Mekan
const PERDE_SLUGS = new Set([
  "stor","zebra","dikey","dikey_ray",
  "ahsap_jaluzi_25","ahsap_jaluzi_50","metal_jaluzi_16_25",
  "plise14","plise25","natty_plise",
  "tul_fon_perde","silhouette","rustik",
  "stor_sistem","ciftli_sistem","sadece_kumas","dosemelik",
  "surme_sineklik","sineklik_dikey","sineklik_duble_yatay",
]);
const GOLGELENDIRME_SLUGS = new Set([
  "zip_art110_helio","skyzip_1400",
  "kasetli_tente","mafsalli_tente","pencere_tentesi",
  "giyotin_cam","cam_tavan","surme_cam_sistemi",
  "pergola","bioklimatik","zip_aksesuar",
]);

const PRODUCT_CATEGORIES = [
  { key: "perde", label: "Perde Sistemleri", subLabel: "İç Mekan", color: "text-blue-500" },
  { key: "golgelendirme", label: "Gölgelendirme", subLabel: "Dış Cephe", color: "text-emerald-500" },
];

export function QuoteBuilderPage({ onClose, brand, editingQuote, onSaved, isRevision = false, saveAsOrder = false, onlyRegisteredCustomers = false }) {
  const { tr, lang } = useContext(UICtx);
  const { user } = useContext(AuthCtx);
  const isAdmin = user?.role === "admin";
  // Fiyat düzenleme yetkisi: admin + satış. BAYİLER ASLA (kendi fiyatını düşürebilir)
  const canEditPrice = user?.role === "admin" || user?.role === "sales";
  const isEdit = !!editingQuote;
  const isRevMode = isRevision && !!editingQuote;
  const [customers, setCustomers] = useState([]);
  const [productGroups, setProductGroups] = useState([]);
  const [assets, setAssets] = useState([]);
  const [priceItems, setPriceItems] = useState([]);
  // Otomatik kumanda satırı: kullanıcı silerse "kumanda istemiyorum" —
  // teklif kaydında yaşar (editingQuote'dan geri okunur; spec: "geri eklenmez")
  const [kumandaOptOut, setKumandaOptOut] = useState(isKumandaOptOut(editingQuote));
  const [rates, setRates] = useState({ eurToTl: 38, usdToTl: 34 });
  const [matrices, setMatrices] = useState([]);

  const [customerId, setCustomerId] = useState(editingQuote?.customerId || "");
  const [customerQuery, setCustomerQuery] = useState("");
  const [showCustDropdown, setShowCustDropdown] = useState(false);
  const [newCustPhone, setNewCustPhone] = useState("");
  const [newCustAddress, setNewCustAddress] = useState("");
  const [projectName, setProjectName] = useState(editingQuote?.projectName || "");
  const [items, setItems] = useState(editingQuote?.items || []);
  const [validDays, setValidDays] = useState(editingQuote?.validDays ?? 15);
  const [notes, setNotes] = useState(editingQuote?.notes || "");
  const [notesTouched, setNotesTouched] = useState(isEdit);
  const [vatExempt, setVatExempt] = useState(editingQuote?.vatExempt || false);
  const [vatExemptNoteText, setVatExemptNoteText] = useState(editingQuote?.vatExemptNote || "3065 sayılı KDV Kanunu Madde 11/1-a kapsamında KDV'den istisnadır");

  const [showProdDropdown, setShowProdDropdown] = useState(false);
  const [productQuery, setProductQuery] = useState("");
  const [prodCategory, setProdCategory] = useState(null); // null | "perde" | "golgelendirme"
  const [activeGroup, setActiveGroup] = useState(null);
  const [draftItem, setDraftItem] = useState(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [productDrawerOpen, setProductDrawerOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(true);

  const [showMontajPrompt, setShowMontajPrompt] = useState(false);
  const [montajDesc, setMontajDesc] = useState("Montaj ve İşçilik");
  const [montajAmount, setMontajAmount] = useState("");
  // Dikiş bedeli prompt
  const [showDikisPrompt, setShowDikisPrompt] = useState(false);
  const [sewingCosts, setSewingCosts] = useState([]);
  const [dikisRow, setDikisRow] = useState({ costId: "", productType: "", qty: 1, unitPrice: 0, unit: "MT" });
  const [saving, setSaving] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [dataLoadError, setDataLoadError] = useState("");
  const [pendingEditIndex, setPendingEditIndex] = useState(null);
  const allProductGroupsRef = useRef([]);
  const [, setStep] = useState(isEdit ? "summary" : "customer"); // "customer" | "product" | "summary"
  const [karOrani, setKarOrani] = useState(editingQuote?.marginPct ?? 35);
  const [itemCosts, setItemCosts] = useState(editingQuote?.itemCosts || {});
  const [selectedBankId, setSelectedBankId] = useState(editingQuote?.bankAccountId || "");
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [dealerGroups, setDealerGroups] = useState([]);
  const [globalMargins, setGlobalMargins] = useState({});
  const [showManualRow, setShowManualRow] = useState(false);
  const [manualRow, setManualRow] = useState({ product: "", unit: "adet", qty: 1, unitPrice: 0, currency: "TL", kdvRate: 20 });
  const [savedQuote, setSavedQuote] = useState(null); // post-save aksiyon barı için
  const [showSavedPreview, setShowSavedPreview] = useState(false);
  const [sendingQuoteEmail, setSendingQuoteEmail] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [preparingShare, setPreparingShare] = useState(false);
  // Gümrük masrafı
  const [customsFeeAmount, setCustomsFeeAmount] = useState("100");
  const [customsDismissed, setCustomsDismissed] = useState(false);
  // Admin fiyat override
  const [qEditItemPriceIdx, setQEditItemPriceIdx] = useState(null);
  const [qEditItemM2Val, setQEditItemM2Val] = useState("");
  const [qEditItemPriceOrig, setQEditItemPriceOrig] = useState(null);
  const [qEditItemPriceVal, setQEditItemPriceVal] = useState("");
  const [qManualKdvStr, setQManualKdvStr] = useState("");
  const [qManualTotalStr, setQManualTotalStr] = useState("");
  const [qbLeftTotalStr, setQbLeftTotalStr] = useState("");
  // Manuel kur override (TL teklif için)
  const [customEurTl, setCustomEurTl] = useState(null); // null = TCMB kuru kullan
  const [editingRate, setEditingRate] = useState(false);
  const [editRateVal, setEditRateVal] = useState("");

  // ── Taslak Otomatik Kayıt ─────────────────────────────────────────────────
  const DRAFT_KEY = "zen_quote_draft";
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);
  const draftSaveRef = useRef(false);

  // Müşteri para birimi odaklı teklif para birimi — müşteriden auto-fill, kullanıcı override edebilir
  const [quoteCurrency, setQuoteCurrency] = useState(editingQuote?.currency || "TL");
  const [formFabricGroupsMap, setFormFabricGroupsMap] = useState(null);

  useEffect(() => {
    (async () => {
      try {
      const [c, pg, a, r, mx, bk, dg, pi] = await Promise.all([
        api.get("/customers"), api.get("/product-groups"), api.get("/assets"), api.get("/rates"), api.get("/matrices"), api.get("/bank-accounts"), api.get("/dealer-groups"), api.get("/prices"),
      ]);
      setCustomers(c.data.filter((x) => x.type !== "Tedarikçi" && x.type !== "Çalışan"));
      allProductGroupsRef.current = pg.data;
      setProductGroups(pg.data.filter((g) => g.active !== false && g.slug !== "tavan_plise"));
      setAssets(a.data); setRates(r.data); setMatrices(mx.data || []);
      setPriceItems(pi.data || []);
      setArtZipMatrices(mx.data || []); setSkyZipMatrices(mx.data || []);
      setBankAccounts(bk.data || []);
      setDealerGroups(dg.data || []);
      // Global kâr marjı varsayılanlarını yükle
      try {
        const gm = await api.get("/settings?category=pricing_defaults");
        const entry = (gm.data || []).find(s => s.key === "global_margins");
        if (entry?.value) setGlobalMargins(entry.value);
      } catch { /* varsayılan kullan */ }
      if (!editingQuote?.bankAccountId) {
        const def = (bk.data || []).find(b => b.isDefault);
        if (def) setSelectedBankId(def.id);
      }
      // formFabricGroups — kumaş-form eşleşme ayarı
      try {
        const stg = await api.get("/settings?category=form_config");
        const entry = (stg.data || []).find(s => s.key === "form_fabric_groups");
        if (entry?.value) setFormFabricGroupsMap(entry.value);
      } catch { /* settings çekilemezse varsayılan mantık kullanılır */ }
      setDataReady(true);
      } catch (error) { setDataLoadError("Ürün bilgileri yüklenemedi. Düzenleyiciyi kapatıp yeniden açabilirsiniz."); }
    })();
  }, []);

  const customer = customers.find((c) => c.id === customerId);
  // targetCur artık quoteCurrency state'inden geliyor (müşteriden auto-fill, manuel override mümkün)
  const targetCur = quoteCurrency;
  // Etkin kur: özel kur varsa onu kullan, yoksa TCMB kurunu kullan
  // (24.10.2026: manuel kur TÜM yönleri ezer — eurSell/eurBuy dahil; ekranda
  //  görünen kur ile hesaplanan tutarlar artık ÇELİŞMEZ — sipariş mat. K3)
  const effectiveRates = customEurTl
    ? { ...rates, eurToTl: customEurTl, eurSell: customEurTl, eurBuy: customEurTl }
    : rates;
  const hasMontaj = items.some((it) => it.isMontaj);

  useEffect(() => {
    if (customer?.vatExemptNote && !isEdit) setVatExemptNoteText(customer.vatExemptNote);
    setCustomsDismissed(false); // müşteri değişince gümrük uyarısını sıfırla
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (customer && !notesTouched) setNotes(defaultQuoteNotes(customer.type === "Yurt Dışı Müşteri", hasMontaj));
  }, [customerId, hasMontaj, dataReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // Montaj eklenip/kaldırıldığında NAKLİYE satırını canlı güncelle (notlar dokunulmuş olsa bile)
  useEffect(() => {
    setNotes(prev => {
      if (!prev) return prev;
      return prev.split("\n").map(line => {
        if (!line.startsWith("NAKLİYE/TRANSPORT:")) return line;
        if (line.includes("EXW") && !line.includes("MONTAJ")) return line; // Yurt dışı
        return `NAKLİYE/TRANSPORT: ${hasMontaj ? "MONTAJ DAHİL" : "MONTAJ HARİÇ"}`;
      }).join("\n");
    });
  }, [hasMontaj]); // eslint-disable-line react-hooks/exhaustive-deps

  // Taslak: sayfa açılışında mevcut taslak kontrolü (sadece yeni teklif modunda)
  useEffect(() => {
    if (isEdit || saveAsOrder) return;
    try {
      const saved = localStorage.getItem(DRAFT_KEY);
      if (!saved) return;
      const draft = JSON.parse(saved);
      if (draft && (draft.items?.length > 0 || draft.customerId)) {
        setPendingDraft(draft);
        setShowDraftBanner(true);
      }
    } catch { /* geçersiz JSON */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Taslak: form değiştikçe otomatik kaydet (sadece yeni teklif modunda)
  useEffect(() => {
    if (!draftSaveRef.current) { draftSaveRef.current = true; return; }
    if (isEdit || saveAsOrder) return;
    if (items.length === 0 && !customerId) { localStorage.removeItem(DRAFT_KEY); return; }
    const draft = { items, customerId, projectName, notes, validDays, quoteCurrency, karOrani, itemCosts, selectedBankId, savedAt: new Date().toISOString() };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  }, [items, customerId, projectName, notes, validDays, quoteCurrency]); // eslint-disable-line react-hooks/exhaustive-deps

  const restoreDraft = () => {
    if (!pendingDraft) return;
    if (pendingDraft.items?.length > 0) setItems(pendingDraft.items);
    if (pendingDraft.customerId) setCustomerId(pendingDraft.customerId);
    if (pendingDraft.projectName) setProjectName(pendingDraft.projectName);
    if (pendingDraft.notes) { setNotes(pendingDraft.notes); setNotesTouched(true); }
    if (pendingDraft.validDays) setValidDays(pendingDraft.validDays);
    if (pendingDraft.quoteCurrency) setQuoteCurrency(pendingDraft.quoteCurrency);
    if (pendingDraft.karOrani) setKarOrani(pendingDraft.karOrani);
    if (pendingDraft.itemCosts) setItemCosts(pendingDraft.itemCosts);
    if (pendingDraft.selectedBankId) setSelectedBankId(pendingDraft.selectedBankId);
    setShowDraftBanner(false);
    setPendingDraft(null);
    toast.success("Taslak geri yüklendi");
  };

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setShowDraftBanner(false);
    setPendingDraft(null);
  };

  const filteredCustomers = customers.filter((c) => ((c.name || "") + " " + (c.phone || "")).toLowerCase().includes(customerQuery.toLowerCase()));

  const selectCustomer = (c) => {
    setCustomerId(c.id);
    setCustomerQuery("");
    setShowCustDropdown(false);
    // SEGMENT FİYATLANDIRMA: bayi → maliyet+%35 | son kullanıcı → maliyet×2
    // Kâr Oranı kutusu segmente göre otomatik ayarlanır; kullanıcı sonra elle değiştirebilir (taban %35)
    setKarOrani((c.pricingSegment || "son_kullanici") === "bayi" ? 35 : 100);
    // Müşterinin para birimini her zaman uygula (yeni veya düzenleme modu fark etmez)
    const newCur = ((c.currency || "TL")).toUpperCase();
    if (newCur !== quoteCurrency) {
      setQuoteCurrency(newCur);
      const sym = { TL: "₺", EUR: "€", USD: "$" }[newCur] || newCur;
      toast.success(`${sym} Para birimi ${newCur} olarak ayarlandı`, {
        description: `${c.name} için kayıtlı para birimi uygulandı.`,
        duration: 3500,
      });
    }
  };
  const createInlineCustomer = async () => {
    if (!customerQuery.trim()) { toast.error("Müşteri adı girin"); return; }
    try {
      const { data } = await api.post("/customers", { name: customerQuery.trim(), type: "Perakende Müşteri", phone: newCustPhone.trim(), address: newCustAddress.trim(), currency: "TL" });
      setCustomers([...customers, data]);
      toast.success(`${data.name} Perakende Müşteri olarak eklendi`);
      selectCustomer(data);
      setNewCustPhone(""); setNewCustAddress("");
    } catch (e) { toast.error("Müşteri eklenemedi"); }
  };

  const filteredGroups = productGroups
    .filter((g) => (g.name || "").toLowerCase().includes(productQuery.toLowerCase()))
    .filter((g) => {
      if (!prodCategory) return true;
      if (prodCategory === "perde") return PERDE_SLUGS.has(g.slug);
      if (prodCategory === "golgelendirme") return GOLGELENDIRME_SLUGS.has(g.slug);
      return true;
    })
    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr"));
  const openProductForm = (grp) => {
    setActiveGroup(grp);
    setEditingItemIndex(null);
    const isDikeyGrp = grp.slug === "dikey";
    setDraftItem({
      groupSlug: grp.slug, product: grp.name, currency: grp.currency,
      unitPrice: grp.basePricePerM2 || 0, unit: "m²",
      kdvRate: grp.defaultKdv ?? 20,
      qty: 1, widthCm: 0, heightCm: 0,
      kasaOlcusu: grp.slug === "surme_sineklik" ? "40x40" : "",
      fabric: "", fabricCode: "", fabricPrice: 0, fabricCurrency: "", ralCode: "", cableDir: "", mounting: "", midFoot: false,
      pliseExtras: [], extrasLabel: "", renkSayisi: 2,
      motor: false, motorLabel: isDikeyGrp ? "Dikey Motor Standart" : "", motorPrice: isDikeyGrp ? 200 : 0, motorCurrency: isDikeyGrp ? "EUR" : grp.currency,
      control: "", controlLabel: "", controlPrice: 0,
      controlSide: "",
      kurdela: false, reduktorlu: false, egimli: false,
      system14: "", geceGunduz: false, renkliKasa: false, roomName: "", note: "",
      profilRengi: "", boya: "", memfez: "",
      zipCesidi: "", sistemTuru: "", balKodu: "", balDurumu: "", montajliKiris: "", logoBaski: "", fabricBrand: "", motorChosen: false,
      dolu: "", kasaRengi: "", kasaKumandaKaplama: "", ciftCamArasi: "",
      urun: "", reduktor: "", zigilSistem: "", dikeyTipi: "", rayTipi: "", slatGenisligi: "", kasaTipi: "", zincirTipi: "",
      slatSize: "", slatVariantKey: "", slatColor: "", slatColorCode: "",
      slaytTipi: isDikeyGrp ? "8.9" : "",
      slatLeftCount: 0, slatLeftFabric: "",
      slatMiddleCount: 0, slatMiddleFabric: "",
      slatRightCount: 0, slatRightFabric: "",
      lineTotal: 0,
    });
    setShowProdDropdown(false); setProductQuery(""); setProdCategory(null);
    setStep("product");
    setProductDrawerOpen(true);
  };
  const cancelProductForm = () => { setActiveGroup(null); setDraftItem(null); setEditingItemIndex(null); };
  const closeProductDrawer = () => {
    setProductDrawerOpen(false);
    setShowProdDropdown(false);
    setStep(items.length > 0 ? "summary" : "customer");
  };
  const deleteDraftProduct = () => {
    cancelProductForm();
    setProductDrawerOpen(false);
    toast.success("Ürün taslağı silindi");
  };

  const addToCart = () => {
    const w = Number(draftItem.widthCm), h = Number(draftItem.heightCm);
    if (activeGroup.slug === METAL_JALUZI_SLUG && !draftItem.slatSize) { toast.error("Metal jaluzi slat ölçüsü seçilmeli"); return; }
    if (activeGroup.slug === "zip_art110_helio" && !draftItem.motorChosen) { toast.error("Motor seçiniz"); return; }
    if (activeGroup.slug === "zip_aksesuar" && !draftItem.product) { toast.error("Ürün seçiniz"); return; }
    if (activeGroup.slug === DIKEY_RAY_SLUG || activeGroup.slug === STOR_SISTEM_SLUG) {
      if (activeGroup.slug === DIKEY_RAY_SLUG && !draftItem.rayTipi) { toast.error("Ray tipi seçilmeli"); return; }
      if (activeGroup.slug === STOR_SISTEM_SLUG) {
        // REÇETE DOĞRULAMA (31.10): motor zorunlu + motor min 60 cm (ham ölçü)
        const recipeErrs = validateStorSistemDraft({
          sistem: draftItem.sistem, kasaTipi: draftItem.kasaTipi,
          widthCm: Number(draftItem.widthCm), qty: draftItem.qty,
          motorName: draftItem.motorLabel || draftItem.motorName,
        });
        if (recipeErrs.length) { toast.error(recipeErrs[0]); return; }
      }
      if (!w) { toast.error("Genişlik gerekli"); return; }
    } else if (activeGroup.slug !== "zip_aksesuar" && activeGroup.slug !== "rustik" && (!w || !h)) { toast.error("En ve boy gerekli"); return; }
    else if (activeGroup.slug === "rustik" && !w) { toast.error("Genişlik gerekli"); return; }
    if (activeGroup.slug === "surme_sineklik" && !draftItem.kasaOlcusu) { toast.error("Kasa ölçüsü seçilmeli"); return; }
    if (activeGroup.maxWidthCm && w > activeGroup.maxWidthCm) { toast.error(`Max en aşıldı: ${activeGroup.maxWidthCm}cm`); return; }
    if (activeGroup.maxHeightCm && h > activeGroup.maxHeightCm) { toast.error(`Max boy aşıldı: ${activeGroup.maxHeightCm}cm`); return; }
    // Kumaşsız stor: mekanizma fiyatını LİSTEDEN çöz (koda gömülü fiyat YASAK — 31.10)
    const storExtra = activeGroup.slug === STOR_SISTEM_SLUG
      ? { ...draftItem, mekanizmaPrice: (findPriceItem(priceItems, (RECIPE_CODES.mekanizma[draftItem.sistem === "motorlu" ? "motorlu" : "manuel"] || {})[draftItem.kasaTipi]) || {}).basePrice }
      : draftItem;
    const calc = computeLineTotal(activeGroup, w, h, draftItem.qty, storExtra, matrices, rates);
    if (activeGroup.slug === STOR_SISTEM_SLUG && calc.noPrice) {
      toast.error("Mekanizma fiyatı bulunamadı — Fiyat Kalemleri'ni kontrol edin (KS-STOR…)");
      return;
    }
    const { total, m2, unitPrice, currency } = calc;
    const itemKdv = (activeGroup.slug === "dikey" && draftItem.dikeyTipi === "PVC Dikey Perde") ? 20 : (activeGroup.defaultKdv ?? 20);
    let motorAdd = 0;
    // ÇİFTLİ SİSTEM = aynı kasada ön+arka 2 stor → 2 motor gerekir
    const motorAdet = activeGroup.slug === "ciftli_sistem" ? 2 : 1;
    if (draftItem.motor && Number(draftItem.motorPrice) > 0) {
      const mCur = draftItem.motorCurrency || currency;
      const mUnit = mCur === currency ? Number(draftItem.motorPrice) : convertPrice(Number(draftItem.motorPrice), mCur, currency, rates);
      motorAdd = mUnit * motorAdet * (draftItem.qty || 1);
    }
    const totalWithMotor = total + motorAdd;

    // KUMAŞSIZ STOR — REÇETE YOLU (31.10): ana mekanizma satırı + bileşen satırları
    // (zincir / ayak kapak / işçilik / motor) + otomatik kumanda. SAF REÇETE
    // FİYATLAMASI: müşteri/bayi/marj katmanları uygulanmaz (kullanıcı kararı:
    // "aynen reçetedeki gibi"); elle fiyat girişi kutsal kalır (düzenlemeden sonra).
    if (activeGroup.slug === STOR_SISTEM_SLUG) {
      const built = buildStorSistemComponents({
        sistem: draftItem.sistem, kasaTipi: draftItem.kasaTipi,
        widthCm: Number(draftItem.widthCm), qty: draftItem.qty,
        motorName: draftItem.motorLabel || draftItem.motorName,
        zincirTipi: draftItem.zincirTipi,
      }, priceItems, assets);
      if (built.main.lineTotal == null) { toast.error(built.errors[0] || "Mekanizma fiyatı bulunamadı"); return; }
      if (built.errors.length) toast.warning(built.errors.join(" · "), { duration: 5000 });
      const mainRow = {
        groupSlug: activeGroup.slug, product: built.main.product,
        currency: "TL", unitPrice: built.main.unitPrice, unit: "mt", qty: built.main.qty,
        widthCm: w, heightCm: Number(draftItem.heightCm) || 0, lineTotal: built.main.lineTotal, computedM2: 0,
        kdvRate: itemKdv, pricingMode: "mt",
        roomName: draftItem.roomName, note: draftItem.note,
        controlSide: draftItem.controlSide, zincirTipi: draftItem.zincirTipi,
        sistem: draftItem.sistem, kasaTipi: draftItem.kasaTipi,
        isManualPrice: false, listLineTotal: built.main.lineTotal, discountPercent: 0,
      };
      const compRows = built.components.map((c) => ({
        groupSlug: "stor_sistem_acc", kind: c.kind, product: c.product,
        currency: c.currency, unitPrice: c.unitPrice, unit: c.unit, qty: c.qty,
        widthCm: 0, heightCm: 0, lineTotal: c.lineTotal, kdvRate: 20,
        isAccessory: true, listLineTotal: c.lineTotal, discountPercent: 0, isManualPrice: false,
      }));
      let nextItems;
      let ruleRes;
      if (editingItemIndex !== null) {
        // Blok yeniden kurulumu — saf yardımcı (eski bileşenler + eski kumanda temizlenir)
        ruleRes = replaceStorItem(items, editingItemIndex, mainRow, compRows,
          { optOut: kumandaOptOut, priceItems, assets });
        nextItems = ruleRes.items;
        if (nextItems.length !== items.length) reindexItemCosts(items, nextItems, editingItemIndex);
      } else {
        ruleRes = applyKumandaRule([...items, mainRow, ...compRows], { optOut: kumandaOptOut, priceItems, assets });
        nextItems = ruleRes.items;
      }
      if (ruleRes.warning) toast.warning(ruleRes.warning, { duration: 5000 });
      setItems(nextItems);
      if (editingItemIndex !== null) {
        toast.success("Kalem güncellendi");
        cancelProductForm();
        setProductDrawerOpen(false);
        return;
      }
      toast.success(`${built.main.product} sepete eklendi`);
      // Yeni satır: 2 sn vurgu + otomatik kaydırma (ilk yeni kalem items.length)
      setFresh({ index: items.length, at: Date.now() });
      // Hızlı Seri Giriş (genel yol ile aynı — 31.10): formu KAPATMA, ölçüleri sıfırla;
      // farklı ürün için formdaki "← Ürün Listesine Geri Dön" yeter
      // (05.11: yalnız EN silinir — BOY ve diğer alanlar korunur, adet 1 döner)
      setDraftItem(prev => prev ? resetDraftAfterAdd(prev) : prev);
      return;
    }

    if (editingItemIndex !== null) {
      const base = draftItem.isAccessory ? total : totalWithMotor;
      // Düzenleme modunda da müşteri özel / bayi grubu fiyatı uygula
      const editPtKey = getProductTypeKey(activeGroup.slug, draftItem);
      const editDG = dealerGroups.find(g => g.id === customer?.dealerGroupId);
      // Plise/Jaluzi için slug-bazlı fabricGroup türet (kumaş seçimi olmayan ürünler için)
      const editFabricGroup = draftItem.fabricGroup ||
        (activeGroup.slug.startsWith("ahsap_jaluzi") || activeGroup.slug === METAL_JALUZI_SLUG ? activeGroup.slug :
         activeGroup.slug === "natty_plise" ? nattyPliseFabricGroupKey(draftItem.nattySeri) :
         activeGroup.slug.startsWith("plise") ? activeGroup.slug : "");
      const editCR = !draftItem.isAccessory ? applyCustomerPricing(customer, editPtKey, editFabricGroup, m2, total, draftItem.qty || 1) : null;
      const editDR = !editCR && !draftItem.isAccessory ? applyDealerGroupPricing(editDG, editPtKey, editFabricGroup, m2, total, draftItem.qty || 1) : null;
      // Matris fiyatlı ürünlerde (Zip vb.) global marj uygulanmaz — liste fiyatı zaten nihai fiyattır
      const isMatrixPriced = activeGroup?.pricingMode === "matrix";
      // Manuel fiyat girilmişse global kâr marjı atlanır
      // MANUEL FİYAT KUTSALDIR: sepetten elle girilen fiyat (isManualPrice) da korunur
      const editHasManualPrice = Number(draftItem.manualUnitPrice) > 0 || draftItem.isManualPrice === true;
      // "Kutsal": sepetten elle girilen satır toplamı — hiçbir fiyat kuralı yeniden hesaplayamaz
      const editSacredManual = draftItem.isManualPrice === true && !(Number(draftItem.manualUnitPrice) > 0);
      const editGM = !editCR && !editDR && !draftItem.isAccessory && !isMatrixPriced && !editHasManualPrice ? applyGlobalMarginPricing(globalMargins, editPtKey, m2, total, draftItem.qty || 1) : null;
      const editApplied = editSacredManual ? null : (editCR || editDR || editGM);
      const editFinal = editSacredManual
        ? (draftItem.lineTotal ?? base)  // manuel satır toplamı aynen korunur (motor dahil)
        : (editApplied ? editApplied.total + motorAdd : base);
      const editDisc = editApplied ? editApplied.discountPct : 0;
      const updated = {
        ...draftItem, widthCm: w, heightCm: h,
        lineTotal: editFinal, computedM2: m2,
        motorTotalInCur: draftItem.isAccessory ? 0 : (editSacredManual ? (draftItem.motorTotalInCur ?? motorAdd) : motorAdd),
        motorQty: motorAdet,
        // unitPrice = nihai birim fiyat (lineTotal/qty) — daima tutarlı
        unitPrice: Math.round(editFinal / Math.max(1, draftItem.qty || 1) * 100) / 100,
        currency, kdvRate: draftItem.isAccessory ? (draftItem.kdvRate ?? 20) : itemKdv,
        listLineTotal: editSacredManual ? (draftItem.listLineTotal ?? draftItem.lineTotal ?? base) : base,
        discountPercent: editDisc, isManualPrice: editHasManualPrice,
        dealerRuleLabel: editApplied?.label || null,
        ruleType: editCR ? "customer" : editDR ? "dealer" : editGM ? "global" : null,
        fabricGroup: editFabricGroup || draftItem.fabricGroup || undefined,
        // Düzenlemede de fiyatlandırma modu korunur — föy m² fiyatını doğru gösterebilsin (K2)
        pricingMode: activeGroup?.pricingMode || draftItem.pricingMode || undefined,
      };
      const newItems = syncLinkedAccessories(
        (() => { const l = [...items]; l[editingItemIndex] = updated; return l; })(),
        editingItemIndex,
        updated
      );
      if (newItems.length !== items.length) {
        // Kumanda/Sensör satırı eklendi/silindi → maliyet indeksleri kaymasın
        reindexItemCosts(items, newItems, editingItemIndex);
      }
      setItems(newItems);
      if (editHasManualPrice) toast("Manuel fiyat kaydedildi — kâr marjı uygulanmadı", { icon: "⚠️", duration: 4000 });
      else if (editApplied?.label) toast.success(`Özel fiyat uygulandı — ${editApplied.label}`, { duration: 4000 });
      else toast.success("Kalem güncellendi");
      cancelProductForm();
      setProductDrawerOpen(false);
      return;
    }

    // Hiyerarşik fiyatlandırma: Müşteri Net/Kâr Marjı → Bayi Grubu → Global Kâr Marjı → Müşteri İskontosu → Liste Fiyatı
    const productTypeKey = getProductTypeKey(activeGroup.slug, draftItem);
    const customerDealerGroup = dealerGroups.find(g => g.id === customer?.dealerGroupId);
    // Plise/Jaluzi için slug-bazlı fabricGroup türet (kumaş seçimi olmayan ürünler için)
    const derivedFabricGroup = draftItem.fabricGroup ||
      (activeGroup.slug.startsWith("ahsap_jaluzi") || activeGroup.slug === METAL_JALUZI_SLUG ? activeGroup.slug :
       activeGroup.slug === "natty_plise" ? nattyPliseFabricGroupKey(draftItem.nattySeri) :
       activeGroup.slug.startsWith("plise") ? activeGroup.slug : "");
    // Manuel fiyat girilmişse global kâr marjı da atlanır
    const hasManualPrice = Number(draftItem.manualUnitPrice) > 0;
    // MANUEL FİYAT KUTSAL (24.10.2026 çift iskonto düzeltmesi): manuel fiyat zaten
    // iskontoyu içine gömer (OrderForm zipIskonto) — üstüne müşteri/bayi kuralı BİNDİRİLMEZ.
    const custResult = !draftItem.isAccessory && !hasManualPrice ? applyCustomerPricing(customer, productTypeKey, derivedFabricGroup, m2, total, draftItem.qty || 1) : null;
    const dealerResult = !custResult && !draftItem.isAccessory && !hasManualPrice ? applyDealerGroupPricing(customerDealerGroup, productTypeKey, derivedFabricGroup, m2, total, draftItem.qty || 1) : null;
    // Matris fiyatlı ürünlerde (Zip vb.) global marj uygulanmaz — liste fiyatı zaten nihai fiyattır
    const globalResult = !custResult && !dealerResult && !draftItem.isAccessory && activeGroup?.pricingMode !== "matrix" && !hasManualPrice ? applyGlobalMarginPricing(globalMargins, productTypeKey, m2, total, draftItem.qty || 1) : null;
    const appliedResult = custResult || dealerResult || globalResult;

    let finalTotal, finalDiscount, appliedDealerLabel;
    if (appliedResult) {
      finalTotal = appliedResult.total + motorAdd;
      finalDiscount = appliedResult.discountPct;
      appliedDealerLabel = appliedResult.label;
    } else {
      // Müşteri bazlı ürün iskontosu (fallback) — manuel fiyatlı kaleme İKİNCİ KEZ iskonto yok
      const autoDiscount = hasManualPrice ? 0 : ((customer?.productDiscounts?.[productTypeKey] != null ? customer.productDiscounts[productTypeKey] : customer?.discountPercent) || 0);
      finalTotal = autoDiscount > 0 ? Math.round(totalWithMotor * (1 - autoDiscount / 100) * 100) / 100 : totalWithMotor;
      finalDiscount = autoDiscount;
      appliedDealerLabel = null;
    }

    const mainItem = {
      ...draftItem, widthCm: w, heightCm: h,
      lineTotal: finalTotal, computedM2: m2, motorTotalInCur: motorAdd, motorQty: motorAdet,
      // unitPrice = nihai birim fiyat (lineTotal/qty) — gösterimde daima tutarlı
      unitPrice: Math.round(finalTotal / Math.max(1, draftItem.qty || 1) * 100) / 100,
      currency, kdvRate: itemKdv,
      listLineTotal: totalWithMotor, discountPercent: finalDiscount, isManualPrice: hasManualPrice,
      dealerRuleLabel: appliedDealerLabel || null,
      ruleType: custResult ? "customer" : dealerResult ? "dealer" : globalResult ? "global" : null,
      fabricGroup: derivedFabricGroup || draftItem.fabricGroup || undefined,
      // Fiyatlandırma modu kalemle birlikte saklanır (K2) — föy m²/adet ayrımını
      // slug tahminine bırakmadan bilir. Bilinmiyorsa alan hiç yazılmaz.
      pricingMode: activeGroup?.pricingMode || draftItem.pricingMode || undefined,
    };
    const newItems = [mainItem];
    if (draftItem.control && (draftItem.controlPrice > 0 || draftItem.controlLabel)) {
      let cPrice = Number(draftItem.controlPrice) || 0;
      // controlPrice=0 ise REMOTE_PRICES'tan otomatik fiyat bul
      if (cPrice === 0 && draftItem.controlLabel) {
        for (const brand of Object.values(REMOTE_PRICES)) {
          const found = brand.find(r => r.label === draftItem.controlLabel);
          if (found) { cPrice = found.price; break; }
        }
      }
      newItems.push({
        groupSlug: activeGroup.slug + "_control", product: `Kumanda - ${draftItem.controlLabel || draftItem.control}`,
        currency: draftItem.controlCurrency || "EUR", unitPrice: cPrice, unit: "adet", qty: draftItem.qty || 1,
        widthCm: 0, heightCm: 0, lineTotal: cPrice * (draftItem.qty || 1), kdvRate: 20,
        isAccessory: true, listLineTotal: cPrice * (draftItem.qty || 1), discountPercent: 0, isManualPrice: false,
      });
    }
    // ── Sensör (Zip formu) — ayrı kalem olarak ekle ──────────────────────────
    if (activeGroup.slug === "zip_art110_helio" && draftItem.sensorLabel && Number(draftItem.sensorPrice) > 0) {
      const sPrice = Number(draftItem.sensorPrice);
      newItems.push({
        groupSlug: activeGroup.slug + "_sensor",
        product: `Sensör - ${draftItem.sensorLabel}`,
        currency: draftItem.sensorCurrency || "EUR",
        unitPrice: sPrice,
        unit: "adet",
        qty: draftItem.qty || 1,
        widthCm: 0, heightCm: 0,
        lineTotal: sPrice * (draftItem.qty || 1),
        kdvRate: 20,
        isAccessory: true,
        listLineTotal: sPrice * (draftItem.qty || 1),
        discountPercent: 0,
        isManualPrice: false,
      });
    }

    // ── Stor / Zebra perde aksesuarları — ayrı kalem olarak ekle ──────────────
    const ROLLER_ACC_SLUGS = ["stor","zebra","silhouette","motorlu_stor","plise25"];
    if (ROLLER_ACC_SLUGS.includes(activeGroup.slug)) {
      const wMT = Math.round(((Number(draftItem.widthCm) || 0) / 100) * 100) / 100;
      const hMT = Math.round(((Number(draftItem.heightCm) || 0) / 100) * 100) / 100;
      const pQty = draftItem.qty || 1;
      const pushAcc = (product, unit, unitQty, unitPrice) => {
        const totalQty = Math.round(unitQty * pQty * 100) / 100;
        const lineTotal = Math.round(unitPrice * totalQty * 100) / 100;
        if (lineTotal <= 0) return;
        newItems.push({ groupSlug: activeGroup.slug + "_acc", product, currency: "TL", unitPrice, unit, qty: totalQty, widthCm: 0, heightCm: 0, lineTotal, kdvRate: 20, isAccessory: true, listLineTotal: lineTotal, discountPercent: 0, isManualPrice: false });
      };
      if (STOR_MEKANIZMA_PRICES[draftItem.mekanizma])
        pushAcc(`Mekanizma — ${draftItem.mekanizma}`, "MT", wMT, STOR_MEKANIZMA_PRICES[draftItem.mekanizma]);
      if (STOR_KASA_FARK[draftItem.kasa])
        pushAcc(`Kasa — ${draftItem.kasa}`, "MT", wMT, STOR_KASA_FARK[draftItem.kasa]);
      if (draftItem.kasaRengi && !STOR_KASA_RENGI_STANDART.includes(draftItem.kasaRengi))
        pushAcc(`Kasa Renk Farkı — ${draftItem.kasaRengi}`, "MT", wMT, STOR_KASA_RENGI_FARKI);
      if (STOR_ALT_CITA_FARK[draftItem.altCita])
        pushAcc(`Alt Çıta — ${draftItem.altCita}`, "MT", wMT, STOR_ALT_CITA_FARK[draftItem.altCita]);
      if (draftItem.yanKanal === "Var")
        pushAcc("Yan Kanal (6.5 Derinlik)", "MT", hMT, STOR_YAN_KANAL_TL_MT);
      if (draftItem.storZigil === "Evet")
        pushAcc("Stor Zegil (Tel Dahil)", "takım", 1, STOR_ZIGIL_TL);
      const etekFark = activeGroup.slug === "zebra" ? ZEBRA_ETEK_FARK : STOR_ETEK_FARK;
      if (etekFark[draftItem.etekModel])
        pushAcc(`Etek Model — ${draftItem.etekModel}`, "MT", wMT, etekFark[draftItem.etekModel]);
      if (STOR_ZINCIR_FARK[draftItem.zincir])
        pushAcc(`Metal Zincir — ${draftItem.zincir}`, "adet", 1, STOR_ZINCIR_FARK[draftItem.zincir]);
      if (draftItem.reduktor === "Var")
        pushAcc("Redüktör", "adet", 1, STOR_REDUKTOR_TL);
      if (draftItem.yayliMekanizma === "Var")
        pushAcc("Yaylı Mekanizma", "takım", 1, STOR_YAYLI_MEK_TL);
      if (draftItem.cocukKilidi === "Var")
        pushAcc("Çocuk Kilidi", "adet", 1, STOR_COCUK_KILIDI_TL);
      if (draftItem.deluxElcek === "Var")
        pushAcc("Delüx Elçek", "adet", 1, DELUX_ELCEK_TL);
      if (LAZER_ETEK_PRICES[draftItem.lazerEtek])
        pushAcc(`Lazer Etek Kesim ${draftItem.lazerEtek}`, "MT", wMT, LAZER_ETEK_PRICES[draftItem.lazerEtek]);
    }

    // ── Ahşap Jaluzi aksesuarları ───────────────────────────────────────────
    const AHSAP_JALUZI_SLUGS = ["ahsap_jaluzi_25", "ahsap_jaluzi_50"];
    if (AHSAP_JALUZI_SLUGS.includes(activeGroup.slug)) {
      const pQty = draftItem.qty || 1;
      const pushAJ = (product, unit, unitQty, unitPrice) => {
        const totalQty = Math.round(unitQty * pQty * 100) / 100;
        const lineTotal = Math.round(unitPrice * totalQty * 100) / 100;
        if (lineTotal <= 0) return;
        newItems.push({ groupSlug: activeGroup.slug + "_acc", product, currency: "TL", unitPrice, unit, qty: totalQty, widthCm: 0, heightCm: 0, lineTotal, kdvRate: 20, isAccessory: true, listLineTotal: lineTotal, discountPercent: 0, isManualPrice: false });
      };
      // Delüx Elçek — "Delüx Elçek" zincir seçilmişse ekle
      if (draftItem.zincir === "Delüx Elçek")
        pushAJ("Delüx Elçek (Ahşap Jaluzi)", "adet", 1, DELUX_ELCEK_TL);
    }

    // ── Tül & Fon Perde — Dikiş Bedeli mtül bazlı ──────────────────────────────
    if (activeGroup.slug === "tul_fon_perde" && draftItem.dikisTuru && Number(draftItem.dikisUnitPrice) > 0) {
      // m2 = mtül/birim (computeLineTotal'dan geldi)
      const mtulBirim = m2 || 0;
      const pQty = draftItem.qty || 1;
      const dikisQty  = Math.round(mtulBirim * pQty * 100) / 100; // toplam mtül
      // Satış fiyatı = maliyet × 1.20
      const dikisSatisFiyat = Math.round(Number(draftItem.dikisUnitPrice) * 1.2 * 100) / 100;
      const dikisTotal = Math.round(dikisSatisFiyat * dikisQty * 100) / 100;
      if (dikisTotal > 0) {
        newItems.push({
          groupSlug: activeGroup.slug + "_dikis",
          product: `Dikiş — ${draftItem.dikisTuru}`,
          currency: "TL", unitPrice: dikisSatisFiyat,
          unit: "mtül", qty: dikisQty,
          widthCm: 0, heightCm: 0, lineTotal: dikisTotal, kdvRate: 20,
          isAccessory: true, listLineTotal: dikisTotal, discountPercent: 0, isManualPrice: false,
        });
      }
    }
    setItems([...items, ...newItems]);
    // Otomatik maliyet (tedarikçi iskonto/matris kayıtlarından) — TEK KAYNAK: utils/segmentPricing
    const autoCostTL = computeItemAutoCost({ slug: activeGroup.slug, draftItem, assets, rates, currency, total });
    if (autoCostTL > 0) setItemCosts(prev => ({ ...prev, [items.length]: autoCostTL }));
    if (hasManualPrice) toast("Manuel fiyat kullanıldı — kâr marjı uygulanmadı", { icon: "⚠️", duration: 4000 });
    else if (appliedDealerLabel) toast.success(`Bayi fiyatı uygulandı — ${appliedDealerLabel}`, { duration: 4500 });
    toast.success(`${activeGroup.name} sepete eklendi`);
    // Yeni satır: 2 sn vurgu + otomatik kaydırma (ilk yeni kalem items.length)
    setFresh({ index: items.length, at: Date.now() });
    // Hızlı Seri Giriş: formu kapatma, sadece EN'yi sıfırla (05.11: BOY kalır, adet 1 döner)
    setDraftItem(prev => prev ? resetDraftAfterAdd(prev) : prev);
  };

  // Kumanda/Sensör satırı eklenip silindiğinde maliyetler indeks anahtarlı
  // olduğu için kayabilir. Yeni listeyi nesne kimliğiyle eski listeye eşleyip
  // maliyetleri doğru indekse taşır. Düzenlenen ana ürün yeni bir nesnedir;
  // onun maliyeti aynı konumdaki eski kalemden alınır.
  const reindexItemCosts = (oldList, newList, editedIdx) => {
    setItemCosts((prev) => {
      const next = {};
      let oi = 0;
      newList.forEach((it, ni) => {
        let found = -1;
        if (ni === editedIdx && oldList[oi] !== it && oldList[oi] && !oldList[oi].isAccessory) {
          found = oi;
        } else {
          for (let k = oi; k < oldList.length; k++) {
            if (oldList[k] === it) { found = k; break; }
          }
        }
        if (found >= 0) {
          if (prev[found] != null) next[ni] = prev[found];
          oi = found + 1;
        }
      });
      return next;
    });
  };

  // Bir ana ürünün hemen ardındaki otomatik aksesuar satırlarını (Kumanda/Sensör)
  // föydeki güncel seçime göre senkronlar:
  //  · seçim kaldırıldıysa satır SİLİNİR (Kumanda Yok → kumanda satırı kalmaz)
  //  · seçim değiştiyse fiyat/etiket/adet güncellenir
  //  · satır yokken kumanda/sensör seçildiyse satır EKLENİR
  // Dönen liste kısalabilir/uzayabilir; çağıran taraf maliyet indekslerini
  // reindexItemCosts ile yeniden anahtarlar.
  const syncLinkedAccessories = (list, mainIdx, mainIt) => {
    if (!mainIt || mainIt.isAccessory) return list;
    const drop = new Set();
    const insertAfter = [];
    const baseSlug = (mainIt.groupSlug || activeGroup.slug || "").replace(/_(control|sensor)$/, "");
    let foundCtrl = false;
    let foundSens = false;
    let j = mainIdx + 1;
    const remotePriceOf = () => {
      let price = Number(mainIt.controlPrice) || 0;
      if (price === 0 && mainIt.controlLabel) {
        for (const brand of Object.values(REMOTE_PRICES)) {
          const found = brand.find(r => r.label === mainIt.controlLabel);
          if (found) { price = found.price; break; }
        }
      }
      return price;
    };
    while (j < list.length && list[j]?.isAccessory) {
      const acc = list[j];
      const slug = acc.groupSlug || "";
      const isCtrl = slug.endsWith("_control");
      const isSens = slug.endsWith("_sensor");
      if (isCtrl) {
        foundCtrl = true;
        if (!mainIt.control) {
          drop.add(j);
        } else {
          const price = remotePriceOf();
          const qty = Math.max(1, Number(mainIt.qty) || 1);
          const total = Math.round(price * qty * 100) / 100;
          const nextProduct = `Kumanda - ${mainIt.controlLabel || mainIt.control}`;
          // Etiket/fiyat/adet değişmediyse satıra dokunma (elle düzenlenmiş
          // fiyat varsa korunur)
          if (acc.product !== nextProduct || Number(acc.unitPrice) !== price || Number(acc.qty) !== qty) {
            list[j] = {
              ...acc,
              product: nextProduct,
              currency: mainIt.controlCurrency || "EUR",
              unitPrice: price, unit: "adet", qty,
              lineTotal: total, listLineTotal: total,
              discountPercent: 0, isManualPrice: false,
            };
          }
        }
      } else if (isSens) {
        foundSens = true;
        if (!mainIt.sensorLabel || !(Number(mainIt.sensorPrice) > 0)) {
          drop.add(j);
        } else {
          const price = Number(mainIt.sensorPrice);
          const qty = Math.max(1, Number(mainIt.qty) || 1);
          const total = Math.round(price * qty * 100) / 100;
          const nextProduct = `Sensör - ${mainIt.sensorLabel}`;
          if (acc.product !== nextProduct || Number(acc.unitPrice) !== price || Number(acc.qty) !== qty) {
            list[j] = {
              ...acc,
              product: nextProduct,
              currency: mainIt.sensorCurrency || "EUR",
              unitPrice: price, unit: "adet", qty,
              lineTotal: total, listLineTotal: total,
              discountPercent: 0, isManualPrice: false,
            };
          }
        }
      }
      j++;
    }
    const insertAt = j;
    if (mainIt.control && !foundCtrl) {
      const price = remotePriceOf();
      if (mainIt.controlLabel || price > 0) {
        const qty = Math.max(1, Number(mainIt.qty) || 1);
        const total = Math.round(price * qty * 100) / 100;
        insertAfter.push({
          groupSlug: `${baseSlug}_control`,
          product: `Kumanda - ${mainIt.controlLabel || mainIt.control}`,
          currency: mainIt.controlCurrency || "EUR", unitPrice: price, unit: "adet", qty,
          widthCm: 0, heightCm: 0, lineTotal: total, kdvRate: 20,
          isAccessory: true, listLineTotal: total, discountPercent: 0, isManualPrice: false,
        });
      }
    }
    if (mainIt.sensorLabel && Number(mainIt.sensorPrice) > 0 && !foundSens) {
      const price = Number(mainIt.sensorPrice);
      const qty = Math.max(1, Number(mainIt.qty) || 1);
      const total = Math.round(price * qty * 100) / 100;
      insertAfter.push({
        groupSlug: `${baseSlug}_sensor`,
        product: `Sensör - ${mainIt.sensorLabel}`,
        currency: mainIt.sensorCurrency || "EUR", unitPrice: price, unit: "adet", qty,
        widthCm: 0, heightCm: 0, lineTotal: total, kdvRate: 20,
        isAccessory: true, listLineTotal: total, discountPercent: 0, isManualPrice: false,
      });
    }
    let out = drop.size ? list.filter((_, idx) => !drop.has(idx)) : list.slice();
    if (insertAfter.length) {
      out = [...out.slice(0, insertAt), ...insertAfter, ...out.slice(insertAt)];
    }
    return out;
  };

  // Birden fazla kalemi tek seferde siler (ana ürün + bağlı aksesuarlar).
  // itemCosts İNDEKS anahtarlı olduğu için silme sonrası kalan kalemler nesne
  // kimliğiyle yeniden anahtarlanır — aksi halde maliyetler kayar.
  // Kumanda/Sensör satırı silinirse ana üründeki seçim de temizlenir; yoksa
  // ürün föyü yeniden kaydedildiğinde satır geri geliyordu.
  const removeItems = (indices) => {
    const drop = new Set(indices);
    // Otomatik kumanda satırı elle silindiyse "kumanda istemiyorum" (31.10) —
    // aynı işleyişte geri eklenmesin diyer YEREL optOut ile kullanılır.
    const optOut = kumandaOptOut || indices.some((idx) => items[idx]?.isKumandaAuto);
    if (optOut !== kumandaOptOut) setKumandaOptOut(optOut);
    const parentFix = new Map();
    indices.forEach((idx) => {
      const it = items[idx];
      if (!it?.isAccessory) return;
      const slug = it.groupSlug || "";
      const isCtrl = slug.endsWith("_control");
      const isSens = slug.endsWith("_sensor");
      if (!isCtrl && !isSens) return;
      let p = idx - 1;
      while (p >= 0 && items[p].isAccessory) p--;
      if (p < 0) return;
      parentFix.set(p, isCtrl
        ? { control: "", controlLabel: "", controlPrice: 0 }
        : { sensor: "", sensorLabel: "", sensorPrice: 0 });
    });
    const remaining = items
      .filter((_, idx) => !drop.has(idx))
      .map((it) => {
        const oldIdx = items.indexOf(it);
        const fix = parentFix.get(oldIdx);
        return fix ? { ...it, ...fix } : it;
      });
    // Kumaşsız stor motorları değiştiyse kumanda kuralı yeniden kurulur (31.10)
    const finalItems = applyKumandaRule(remaining, { optOut, priceItems, assets }).items;
    setItemCosts((prev) => {
      const next = {};
      items.forEach((it, oldIdx) => {
        if (drop.has(oldIdx)) return;
        const newIdx = finalItems.indexOf(it);
        if (newIdx >= 0 && prev[oldIdx] != null) next[newIdx] = prev[oldIdx];
      });
      return next;
    });
    setItems(finalItems);
  };

  const removeItem = (i) => { removeItems([i]); };

  // Ctrl+D / "⧉ Çoğalt" — kalemı çoğalt: kopya listenin SONUNA eklenir
  // (giriş sırası korunur). itemCosts indeks anahtarlı olduğu için maliyet de
  // yeni indekse taşınır (append mevcut indeksleri kaydırmaz).
  const duplicateItemAt = (i) => {
    const src = items[i];
    if (!src) return;
    const newIdx = items.length;
    const cost = itemCosts[i];
    setItems(prev => (i >= 0 && i < prev.length ? [...prev, duplicateItem(prev[i])] : prev));
    if (cost != null) setItemCosts(prevC => ({ ...prevC, [newIdx]: cost }));
    toast.success("Kalem çoğaltıldı");
  };

  // Ctrl+D = çoğalt (05.11): düzenleme modunda DÜZENLENEN kalem, değilse son
  // kalem; metin girişinde (not/arama) ÇALIŞMAZ — tarayıcı yer imi engellenmez.
  useEffect(() => {
    const h = (e) => {
      if (!isDuplicateShortcut(e)) return;
      if (isTextEntryTarget(e.target)) return;
      e.preventDefault();
      duplicateItemAt(duplicateTargetIndex(editingItemIndex, items.length));
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, itemCosts, editingItemIndex]);

  // Yeni eklenen satır vurgusu (05.11): 2 sn "fresh" + otomatik kaydırma.
  // Zamanlayıcı state temizliğiyle yürür (tekrar eklemede yarış yok).
  const [fresh, setFresh] = useState(null); // {index, at}
  useEffect(() => {
    if (!fresh) return;
    const t = setTimeout(() => setFresh(null), 2000);
    return () => clearTimeout(t);
  }, [fresh]);
  useEffect(() => {
    if (!fresh) return;
    // Yalnız kullanıcı zaten listenin altındaysa kaydır — liste kullanıcıyı
    // aşağı çekmesin (05.11 geri bildirimi: "liste kaydıramıyorum")
    const scroller = document.querySelector('[data-testid="quote-products-list-scroll"]');
    const el = document.querySelector(`[data-testid="quote-cart-item-${fresh.index}"]`);
    if (!el || !el.scrollIntoView) return;
    if (scroller && !shouldAutoScroll(scroller.scrollTop, scroller.scrollHeight, scroller.clientHeight)) return;
    el.scrollIntoView({ block: "nearest" });
  }, [fresh]);

  // ── D&D Sıralama ─────────────────────────────────────────────────────────
  const dragGiRef = useRef(null);
  const [draggingGi, setDraggingGi] = useState(null);

  // Kalemleri grupla: ana ürün + hemen ardından gelen aksesuarları
  const itemGroups = useMemo(() => {
    const g = [];
    items.forEach((it, i) => {
      if (it.isAccessory) {
        const last = g[g.length - 1];
        if (last?.type === "main") last.subs.push({ i, it });
        else g.push({ type: "service", i, it });
      } else {
        const isService = it.isMontaj || it.isManual || it.isDikis;
        g.push(isService ? { type: "service", i, it } : { type: "main", i, it, subs: [] });
      }
    });
    return g;
  }, [items]);

  // Sepet görünümü (05.11): özet şeridi + arama. Filtre YALNIZ görünümü daraltır;
  // veri/sıra değişmez, sürükleme ASIL gi indeksleriyle çalışır.
  const [cartQuery, setCartQuery] = useState("");
  const visibleGroups = useMemo(() => {
    const q = cartQuery.trim().toLocaleLowerCase("tr");
    const pairs = itemGroups.map((g, gi) => ({ g, gi }));
    if (!q) return pairs;
    return pairs.filter(({ g }) => {
      const name = String(g.it?.product || g.it?.productName || "").toLocaleLowerCase("tr");
      const room = String(g.it?.roomName || "").toLocaleLowerCase("tr");
      const subs = (g.subs || []).map(s => String(s.it?.product || "").toLocaleLowerCase("tr")).join(" ");
      return name.includes(q) || room.includes(q) || subs.includes(q);
    });
  }, [itemGroups, cartQuery]);
  // Toplam m² — kanonik alan tanımı (orders.py ile birebir):
  // alan = computedM2 veya (En×Boy/10000); toplam = Σ alan×qty
  const cartTotalM2 = items.reduce((s, it) => {
    const area = Number(it.computedM2) || ((Number(it.widthCm) || 0) * (Number(it.heightCm) || 0)) / 10000;
    return s + area * (Number(it.qty) || 1);
  }, 0);

  // Adet +/− (05.11): fiyat, ekleme akışının AYNI fiyat motoruyla yeniden
  // hesaplanır (computeLineTotal + motor eklemi + kumaşsız stor reçete extra'sı);
  // manuel fiyatlı kalemlerde orantısal (kutsal fiyat). Maliyet functionel
  // güncellenir (hızlı tıklamada bayat closure yarışı yok — 05.11 incelemesi).
  const changeQty = (i, delta) => {
    const src = items[i];
    if (!src) return;
    const oldQ = Number(src.qty) || 1;
    const newQ = Math.max(1, oldQ + delta);
    if (newQ === oldQ) return;
    const ratio = newQ / oldQ;
    const r2 = (x) => Math.round(x * 100) / 100;
    const engineTotal = (it) => {
      if (it.isManualPrice || it.groupSlug === "manual") return null;
      const grp = productGroups.find(g => g.slug === it.groupSlug);
      if (!grp) return null;
      try {
        const extra = it.groupSlug === STOR_SISTEM_SLUG
          ? { ...it, mekanizmaPrice: (findPriceItem(priceItems, (RECIPE_CODES.mekanizma[it.sistem === "motorlu" ? "motorlu" : "manuel"] || {})[it.kasaTipi]) || {}).basePrice }
          : it;
        const calc = computeLineTotal(grp, Number(it.widthCm) || 0, Number(it.heightCm) || 0, newQ, extra, matrices, effectiveRates);
        if (!calc || calc.noPrice) return null;
        let tot = Number(calc.total);
        if (!Number.isFinite(tot)) return null;
        if (it.motor && Number(it.motorPrice) > 0) {
          const motorAdet = it.groupSlug === "ciftli_sistem" ? 2 : 1;
          const mCur = it.motorCurrency || calc.currency;
          const mUnit = mCur === calc.currency ? Number(it.motorPrice) : convertPrice(Number(it.motorPrice), mCur, calc.currency, effectiveRates);
          tot += mUnit * motorAdet * newQ;
        }
        return r2(tot);
      } catch { return null; }
    };
    setItems(prev => prev.map((it, idx) => {
      if (idx !== i) return it;
      const scaled = scaleItemQty(it, newQ);
      const eng = engineTotal(it);
      return eng != null ? { ...scaled, lineTotal: eng } : scaled;
    }));
    setItemCosts(prevC => {
      const cost = prevC[i];
      if (cost == null) return prevC;
      return { ...prevC, [i]: r2(cost * ratio) };
    });
  };

  const onGroupDragStart = (gi) => (e) => {
    dragGiRef.current = gi;
    setDraggingGi(gi);
    e.dataTransfer.effectAllowed = "move";
  };
  const onGroupDragOver = (gi) => (e) => {
    e.preventDefault();
    if (dragGiRef.current === null || dragGiRef.current === gi) return;
    const from = dragGiRef.current;
    const newGroups = [...itemGroups];
    const [removed] = newGroups.splice(from, 1);
    newGroups.splice(gi, 0, removed);
    dragGiRef.current = gi;
    const newItems = newGroups.flatMap(grp =>
      grp.type === "service" ? [grp.it] : [grp.it, ...grp.subs.map(s => s.it)]
    );
    // itemCosts TAKİBİ: aynı nesne referanslarıyla indeks eşlemesi (24.10.2026 — sipariş mat. K2)
    setItemCosts((prev) => {
      const next = {};
      newItems.forEach((it, newIdx) => {
        const oldIdx = items.indexOf(it);
        if (oldIdx >= 0 && prev[oldIdx] != null) next[newIdx] = prev[oldIdx];
      });
      return next;
    });
    setItems(newItems);
  };
  const onGroupDrop = async () => {
    setDraggingGi(null);
    dragGiRef.current = null;
    // Mevcut teklif düzenleniyorsa anında kaydet
    if (editingQuote?.id && !isRevMode) {
      try { await api.put(`/quotes/${editingQuote.id}`, { items }); }
      catch { /* sessiz — kullanıcı kaydet tuşuyla da kaydedebilir */ }
    }
  };
  const onGroupDragEnd = () => { setDraggingGi(null); dragGiRef.current = null; };
  const editItem = (i) => {
    // Catalog images can take longer than the already visible cart to load.
    // Queue early clicks instead of reporting a nonexistent missing group.
    if (!dataReady) { setPendingEditIndex(i); return; }
    // KORUMA: föy açıkken başka kalem düzenlenirse kaydedilmemiş form verisi kaybolur
    if (productDrawerOpen && editingItemIndex !== i &&
        !window.confirm("Açık föydeki kaydedilmemiş değişiklikler kaybolacak. Devam edilsin mi?")) return;
    const it = items[i];
    if (it.isAccessory || it.isMontaj) { toast.error("Bu kalem tabloda doğrudan düzenlenebilir"); return; }
    const baseSlug = (it.groupSlug || "").replace(/_motor$|_control$/, "");
    const g = allProductGroupsRef.current.find((pg) => pg.slug === baseSlug);
    if (!g) { toast.error("Ürün grubu bulunamadı"); return; }
    setActiveGroup(g); setDraftItem({ ...it }); setEditingItemIndex(i); setStep("product"); setProductDrawerOpen(true);
  };
  useEffect(() => {
    if (dataReady && pendingEditIndex !== null) {
      editItem(pendingEditIndex);
      setPendingEditIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady, pendingEditIndex]);
  const updateItemDiscount = (i, val) => {
    const pct = Math.max(0, Math.min(100, Number(val) || 0));
    const newItems = [...items];
    const base = newItems[i].listLineTotal ?? newItems[i].lineTotal;
    const newTotal = base * (1 - pct / 100);
    newItems[i] = { ...newItems[i], listLineTotal: base, discountPercent: pct, isManualPrice: false, lineTotal: newTotal, unitPrice: newTotal / (newItems[i].qty || 1) };
    setItems(newItems);
  };
  const updateItemManualPrice = (i, val) => {
    // Kullanıcı teklifin para biriminde (quoteCurrency) bir değer girebilir;
    // geri dönüşümle kalemin native currency'sine çevir
    const amtInQuote = Math.max(0, Number(val) || 0);
    const amtNative = convertPrice(amtInQuote, quoteCurrency, items[i].currency, rates);
    const newItems = [...items];
    const base = newItems[i].listLineTotal ?? newItems[i].lineTotal;
    const pct = base > 0 ? Math.round((1 - amtNative / base) * 1000) / 10 : 0;
    newItems[i] = { ...newItems[i], listLineTotal: base, isManualPrice: true, discountPercent: pct, lineTotal: amtNative, unitPrice: amtNative / (newItems[i].qty || 1) };
    setItems(newItems);
  };
  // Admin: kalem fiyatı satır içi düzenleme (native currency)
  // m² ürünler için: kullanıcı EUR/m² girer, sistem m²×birim hesaplar
  const commitItemPriceQB = (i) => {
    const val = parseFloat((qEditItemPriceVal || "").replace(",", "."));
    if (isFinite(val) && val >= 0) {
      applyItemPriceLive(i, val);
      toast.success("Fiyat güncellendi — manuel fiyat korunur");
    }
    setQEditItemPriceIdx(null);
    setQEditItemPriceVal("");
    setQEditItemM2Val("");
    setQEditItemPriceOrig(null);
  };
  // ANLIK manuel fiyat: kullanıcı yazarken kalem ve teklif toplamları anında güncellenir.
  // newLineTotal = satır toplamı (motor dahil). MANUEL FİYAT KUTSALDIR (isManualPrice=true).
  const applyItemPriceLive = (i, newLineTotal) => {
    if (!isFinite(newLineTotal) || newLineTotal < 0) return;
    const newItems = [...items];
    const it = newItems[i];
    newItems[i] = {
      ...it,
      lineTotal: newLineTotal,
      unitPrice: newLineTotal / Math.max(1, it.qty || 1),
      isManualPrice: true,
      listLineTotal: it.listLineTotal ?? it.lineTotal,
    };
    setItems(newItems);
  };
  // ESC: düzenlemeye başlarkenki fiyat geri yüklenir (canlı değişikliklerden vazgeç)
  const revertItemPriceQB = (i) => {
    if (qEditItemPriceOrig) {
      const newItems = [...items];
      newItems[i] = { ...newItems[i], ...qEditItemPriceOrig };
      setItems(newItems);
    }
    setQEditItemPriceIdx(null);
    setQEditItemPriceVal("");
    setQEditItemM2Val("");
    setQEditItemPriceOrig(null);
  };
  // Kâr Oranı canlı hesaplama — karOrani veya itemCosts değiştiğinde maliyeti bilinen kalemleri fiyatla
  // ÖNEMLI: itemCosts her zaman TL cinsindendir. Kalem fiyatı EUR ise önce TL→EUR çevirisi yapılır.
  // KORUMA: manuel fiyatlar, müşteri/bayiye özel fiyatlar ve iskonto fiyatlarına dokunulmaz.
  useEffect(() => {
    const rate = Number(karOrani);
    if (!rate || rate <= 0) return;
    // SEGMENT FİYATLANDIRMA — TEK KAYNAK: utils/segmentPricing (Sipariş Modalı ile aynı)
    setItems(prev => applySegmentPricing(prev, itemCosts, rates, rate));
  }, [karOrani, itemCosts]); // eslint-disable-line react-hooks/exhaustive-deps
  const addMontajFee = () => {
    const amt = Number(montajAmount);
    if (!montajDesc.trim() || !amt || amt <= 0) { toast.error("Açıklama ve geçerli bir tutar girin"); return; }
    setItems([...items, {
      groupSlug: "montaj", product: montajDesc.trim(), currency: targetCur, unitPrice: amt, unit: "adet",
      qty: 1, widthCm: 0, heightCm: 0, lineTotal: amt, kdvRate: 20, isMontaj: true,
      listLineTotal: amt, discountPercent: 0, isManualPrice: true,
    }]);
    setShowMontajPrompt(false); setMontajDesc("Montaj ve İşçilik"); setMontajAmount("");
    toast.success("Montaj bedeli sepete eklendi");
  };

  const openDikisPrompt = async () => {
    if (sewingCosts.length === 0) {
      try {
        const { data } = await api.get("/sewing-costs");
        setSewingCosts(data || []);
      } catch { /* sessizce devam */ }
    }
    setDikisRow({ costId: "", productType: "", qty: 1, unitPrice: 0, unit: "MT" });
    setShowDikisPrompt(true);
  };

  const addDikisFee = () => {
    if (!dikisRow.productType || !dikisRow.qty || dikisRow.qty <= 0) {
      toast.error("Dikiş türü ve geçerli bir miktar girin"); return;
    }
    const lineTotal = Number(dikisRow.unitPrice) * Number(dikisRow.qty);
    const newIndex = items.length;
    setItems([...items, {
      groupSlug: "dikis", product: `Dikiş: ${dikisRow.productType}`, currency: "TL",
      unitPrice: Number(dikisRow.unitPrice), unit: dikisRow.unit || "MT",
      qty: Number(dikisRow.qty), widthCm: 0, heightCm: 0, lineTotal,
      kdvRate: 20, isDikis: true,
      listLineTotal: lineTotal, discountPercent: 0, isManualPrice: true,
    }]);
    // F6: Dikiş alış fiyatı = dikiş fiyatı (kullanıcı onayladı)
    if (lineTotal > 0) setItemCosts(prev => ({ ...prev, [newIndex]: Math.round(lineTotal) }));
    setShowDikisPrompt(false);
    toast.success("Dikiş bedeli sepete eklendi");
  };

  // Gümrük masrafı ekle
  const isIntlCustomer = customer?.type === "Yurt Dışı Müşteri" ||
    (customer?.country && !["türkiye", "turkey", "tr"].includes((customer.country || "").toLowerCase().trim()));
  const hasCustomsFee = items.some(it => it.groupSlug === "customs_fee");
  const showCustomsBanner = isIntlCustomer && !hasCustomsFee && !customsDismissed;

  const addCustomsFee = () => {
    const amount = parseFloat(customsFeeAmount) || 100;
    setItems(prev => [...prev, {
      groupSlug: "customs_fee",
      product: "Gümrük Masrafı",
      currency: "EUR",
      unitPrice: amount,
      qty: 1,
      lineTotal: amount,
      listLineTotal: amount,
      isManual: true,
      isAccessory: true,
      kdvRate: 0,
      unit: "Ad",
      widthCm: 0,
      heightCm: 0,
      discountPercent: 0,
      isManualPrice: true,
    }]);
    toast.success(`${amount} EUR gümrük masrafı eklendi`);
  };

  const addManualRow = () => {
    if (!manualRow.product.trim()) { toast.error("Ürün / açıklama zorunlu"); return; }
    const unitPrice = Number(manualRow.unitPrice) || 0;
    const qty = Number(manualRow.qty) || 1;
    const lineTotal = unitPrice * qty;
    setItems([...items, {
      groupSlug: "manual", product: manualRow.product.trim(),
      currency: manualRow.currency || targetCur,
      unitPrice, unit: manualRow.unit || "adet", qty,
      widthCm: 0, heightCm: 0, lineTotal, kdvRate: Number(manualRow.kdvRate) || 20,
      isManual: true, listLineTotal: lineTotal, discountPercent: 0, isManualPrice: true,
    }]);
    setShowManualRow(false);
    setManualRow({ product: "", unit: "adet", qty: 1, unitPrice: 0, currency: targetCur, kdvRate: 20 });
    toast.success("Manuel kalem eklendi");
  };

  // Toplam/KDV hesabi tek kaynakta: src/utils/quoteTotals.js
  // (Python ikizi services/quote_totals.py — parite: test_quote_totals_parity.py;
  //  sohbet araclari da ayni hesabi kullanir)
  const parseManualNum = (s) => {
    const n = parseFloat(String(s || "").replace(",", "."));
    return isFinite(n) ? n : null;
  };
  const manualVat = isAdmin && qManualKdvStr.trim() ? parseManualNum(qManualKdvStr) : null;
  const manualTotal = isAdmin && qManualTotalStr.trim() ? parseManualNum(qManualTotalStr) : null;
  const t = computeQuoteTotals(items, { rates: effectiveRates, targetCur, vatExempt, manualVat, manualTotal });
  const subtotal = t.itemsSubtotal;
  const total = t.grossTotal;
  const autoVatAmt = t.autoVat;
  const kdvGroups = t.kdvGroups;
  const effectiveKdvQB = t.vatAmount;
  const effectiveTotalQB = t.total;
  const effectiveSubtotalQB = t.subtotal;
  const effectiveVatBreakdownQB = t.vatBreakdown;

  // Toplam değişince kalemleri orantısal güncelle
  const redistributeByTotal = (newTotalStr) => {
    const newTotal = parseFloat((newTotalStr || "").replace(",", ".").replace(/[^\d.]/g, ""));
    if (!isFinite(newTotal) || newTotal <= 0 || items.length === 0) return;
    const current = effectiveTotalQB;
    if (!current || current <= 0) return;
    const ratio = newTotal / current;
    setItems(prev => prev.map(it => ({
      ...it,
      lineTotal: Math.round(it.lineTotal * ratio * 100) / 100,
      ...(it.listLineTotal != null ? { listLineTotal: Math.round(it.listLineTotal * ratio * 100) / 100 } : {}),
      isManualPrice: true,
    })));
    setQManualTotalStr("");
    setQManualKdvStr("");
  };

  const saveQuote = async () => {
    if (!dataReady) { toast.error("Veriler yükleniyor, lütfen birkaç saniye sonra tekrar deneyin"); return; }
    if (!customerId) { toast.error("Müşteri seçin veya yeni müşteri ekleyin"); return; }
    if (items.length === 0) { toast.error("En az bir ürün ekleyin"); return; }
    const customerName = customer?.name || (isEdit ? editingQuote.customerName : "");
    if (!customerName) { toast.error("Müşteri bilgisi henüz yükleniyor, lütfen birkaç saniye sonra tekrar deneyin"); return; }
    setSaving(true);

    // ── Sipariş olarak kaydet (OrdersModule'den açıldığında) ─────────────────
    if (saveAsOrder) {
      const orderPayload = {
        customerId, customerName, items, subtotal: effectiveSubtotalQB, discountPercent: 0,
        vatBreakdown: effectiveVatBreakdownQB,
        kumandaOptOut,
        vatAmount: effectiveKdvQB, total: effectiveTotalQB,
        vatExempt, vatExemptNote: vatExempt ? vatExemptNoteText : "",
        currency: targetCur, notes, projectName, rates: effectiveRates,
        status: "teklif",
        ...(isAdmin && (qManualKdvStr.trim() || qManualTotalStr.trim()) ? { manualPriceOverride: true } : {}),
      };
      try {
        if (editingQuote?.id) {
          await api.put(`/orders/${editingQuote.id}`, orderPayload);
          toast.success("Sipariş güncellendi");
        } else {
          const res = await api.post("/orders", orderPayload);
          toast.success("Sipariş oluşturuldu");
          // CRM LEAD BAĞLANTISI: sipariş CRM'den açıldıysa lead'e bağla (tek form akışı)
          if (editingQuote?._leadId && res?.data?.id) {
            try { await api.put(`/crm/leads/${editingQuote._leadId}`, { orderId: res.data.id }); } catch { /* bağlantı opsiyonel */ }
          }
        }
        if (onSaved) onSaved();
        if (onClose) onClose();
      } catch (e) {
        toast.error(apiErrorMessage(e, "Sipariş kaydedilemedi"));
      }
      setSaving(false);
      return;
    }

    // ── Teklif olarak kaydet (normal QuoteBuilder akışı) ────────────────────
    const payload = {
      brand, customerId, customerName, projectName, items, subtotal: effectiveSubtotalQB, total: effectiveTotalQB,
      vatAmount: effectiveKdvQB,
      vatBreakdown: effectiveVatBreakdownQB,
      vatExempt, vatExemptNote: vatExempt ? vatExemptNoteText : "",
      currency: targetCur, notes,
      kumandaOptOut,
      validDays: Number(validDays) || 15,
      rates,
      itemCosts: Object.keys(itemCosts).length > 0 ? itemCosts : null,
      marginPct: Number(karOrani) || 30,
      bankAccountId: selectedBankId || null,
      ...(isAdmin && (qManualKdvStr.trim() || qManualTotalStr.trim()) ? { manualPriceOverride: true } : {}),
    };
    // Bağlantı kopukluğunda 1 kez otomatik yeniden dene
    const trySave = async () => {
      if (isRevMode) {
        // Revizyon modu: mevcut teklifi arşivle, yeni revizyon oluştur
        const res = await api.post(`/quotes/${editingQuote.id}/revise`, payload);
        return res.data;
      } else if (isEdit) {
        const res = await api.put(`/quotes/${editingQuote.id}`, payload);
        return res.data;
      } else {
        const res = await api.post("/quotes", payload);
        return res.data;
      }
    };
    try {
      let data;
      try {
        data = await trySave();
      } catch (firstErr) {
        const isNetworkErr = !firstErr?.response || firstErr?.code === "ERR_NETWORK" || firstErr?.response?.status >= 500;
        if (isNetworkErr) {
          await new Promise(r => setTimeout(r, 1500));
          data = await trySave(); // 2. deneme
        } else {
          throw firstErr;
        }
      }
      const revLabel = data.revisionNo > 1 ? ` · Rev ${data.revisionNo}` : "";
      toast.success(isRevMode ? `Teklif #${data.quoteNo}${revLabel} olarak revize edildi` : isEdit ? `Teklif #${data.quoteNo} güncellendi` : `Teklif #${data.quoteNo} kaydedildi`);
      if (!isEdit && !isRevMode) localStorage.removeItem(DRAFT_KEY); // Taslağı temizle
      setSavedQuote(data); // post-save aksiyon barını göster; onSaved kullanıcı "Kapat" deyince çağrılır
    } catch (e) {
      const msg = apiErrorMessage(e, "Teklif kaydedilemedi");
      const isServer = !e?.response || e?.response?.status >= 500;
      toast.error(isServer ? `Sunucu geçici olarak meşgul, lütfen tekrar deneyin. (${msg})` : msg);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[70] zen-quote-builder zen-hc-workspace flex flex-col" data-testid="quote-builder-page">
      {!dataReady && <div role={dataLoadError ? "alert" : "status"} data-testid="quote-catalog-loading" className="shrink-0 px-4 py-2 text-xs bg-sky-50 text-sky-800 dark:bg-sky-950 dark:text-sky-200">{dataLoadError || (pendingEditIndex !== null ? "Ürün bilgileri yükleniyor; seçtiğiniz perde açılacak…" : "Ürün ve kumaş bilgileri yükleniyor…")}</div>}
      {/* ── Taslak Banner ── yalnız el değmemiş yeni oturumda (05.11: ince şerit) ── */}
      {showDraftRestoreBanner({ showBanner: showDraftBanner, pendingDraft, itemsCount: items.length, customerId, isEdit, saveAsOrder }) && !productDrawerOpen && (
        <div className="shrink-0 bg-amber-500 text-white px-3 py-1.5 flex items-center justify-between gap-3 text-xs z-[80]" data-testid="quote-draft-banner">
          <div className="flex items-center gap-2 min-w-0">
            <Clock className="w-4 h-4 shrink-0" />
            <span className="font-semibold truncate">
              Kaydedilmemiş taslak bulundu
              {pendingDraft.savedAt && (
                <span className="font-normal opacity-80 ml-1">
                  — {new Date(pendingDraft.savedAt).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              {pendingDraft.items?.length > 0 && (
                <span className="font-normal opacity-80 ml-1">({pendingDraft.items.length} kalem)</span>
              )}
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={restoreDraft} className="px-2.5 py-0.5 bg-white text-amber-600 rounded-md font-bold text-[11px] hover:bg-amber-50 transition-colors" data-testid="quote-draft-restore-btn">
              Devam Et
            </button>
            <button onClick={discardDraft} className="px-2.5 py-0.5 bg-amber-600 text-white rounded-md font-semibold text-[11px] hover:bg-amber-700 transition-colors border border-white/30" data-testid="quote-draft-discard-btn">
              Yoksay
            </button>
          </div>
        </div>
      )}
      <div className={`flex-1 min-h-0 overflow-hidden zen-hc-workspace ${productDrawerOpen ? "sm:pr-[min(684px,calc(100vw-384px))]" : ""}`} data-testid="quote-builder-workspace">
        <div className={`h-full grid transition-all duration-300 ${productDrawerOpen ? 'grid-cols-1' : (rightPanelOpen ? 'grid-cols-1 lg:grid-cols-[minmax(360px,51%)_1fr]' : 'grid-cols-1')}`} data-testid="quote-split-view">
          <aside className={`min-h-0 border-r flex flex-col zen-hc-surface ${showProdDropdown ? "relative z-[80]" : ""}`} data-testid="quote-products-panel">
            <div className="shrink-0 px-4 sm:px-5 py-4 border-b border-slate-200 dark:border-[#22304A] flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0 flex items-center gap-3">
                <button onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-200 dark:hover:bg-[#22304A] text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors shrink-0" title="Teklif yazımını kapat" data-testid="quote-left-close-btn">
                  <X className="w-4 h-4" />
                </button>
                <div>
                  <div className="text-[10px] font-black uppercase tracking-[0.22em] text-[#E5322A]">Canlı Sepet</div>
                  <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white leading-tight" data-testid="quote-products-title">Ürün Listesi</h1>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!rightPanelOpen && (
                  <button onClick={() => setRightPanelOpen(true)} className="zen-btn-primary !text-xs shrink-0" data-testid="quote-open-form-btn">
                    <FileSignature className="w-3.5 h-3.5" /> {saveAsOrder ? "Siparişi Tamamla" : "Teklifi Tamamla"}
                  </button>
                )}
                <div className="relative shrink-0">
                <button
                  data-testid="quote-add-product-btn"
                  onClick={() => {
                    if (onlyRegisteredCustomers && !customerId) {
                      toast.error("Ürün eklemeden önce müşteri seçin");
                      return;
                    }
                    setShowProdDropdown(v => !v);
                  }}
                  className="zen-btn-primary !text-xs shrink-0">
                  <Plus className="w-3.5 h-3.5" /> Ürün Ekle
                </button>
            {showProdDropdown && (
              <>
                <div className="fixed inset-0 z-[9998]" onClick={() => { setShowProdDropdown(false); setProdCategory(null); setProductQuery(""); }} data-testid="quote-product-dropdown-backdrop" />
                <div
                  className="absolute z-[9999] top-full right-0 mt-1.5 w-80 max-w-[calc(100vw-2rem)] zen-panel shadow-xl flex flex-col"
                  data-testid="quote-product-dropdown">
                  {/* Kategori seçici — 2 ana grup */}
                  <div className="flex border-b border-slate-100 dark:border-[#22304A]" data-testid="quote-product-category-tabs">
                    {PRODUCT_CATEGORIES.map(cat => (
                      <button key={cat.key} data-testid={`quote-cat-${cat.key}`}
                        onClick={(e) => { e.stopPropagation(); setProdCategory(v => v === cat.key ? null : cat.key); setProductQuery(""); }}
                        className={`flex-1 py-2 px-1 text-center border-b-2 transition-all ${prodCategory === cat.key ? `border-current ${cat.color} font-bold` : "border-transparent text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"}`}>
                        <div className={`text-[10px] font-black uppercase tracking-wider ${prodCategory === cat.key ? cat.color : ""}`}>{cat.label}</div>
                        <div className="text-[9px] opacity-60">{cat.subLabel}</div>
                      </button>
                    ))}
                  </div>
                  {/* Arama */}
                  <input autoFocus data-testid="quote-product-search-input"
                    className="zen-input !rounded-none border-0 border-b text-xs"
                    placeholder={prodCategory ? `${PRODUCT_CATEGORIES.find(c=>c.key===prodCategory)?.label} içinde ara…` : tr("Tüm ürünlerde ara…")}
                    value={productQuery} onChange={(e) => setProductQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()} />
                  {/* Ürün listesi */}
                  <div className="max-h-72 overflow-y-auto">
                    {(() => {
                      if (prodCategory) {
                        // Sadece seçili kategori
                        return filteredGroups.length === 0
                          ? <div className="px-3 py-4 text-center text-xs text-slate-500" data-testid="quote-product-empty">{tr("Eşleşen ürün grubu yok")}</div>
                          : filteredGroups.map((g) => (
                              <button key={g.id} data-testid={`quote-sel-prod-${g.slug}`} onClick={() => openProductForm(g)}
                                className="w-full text-left px-3 py-2 hover:bg-[#E5322A]/5 border-b border-slate-100 dark:border-[#22304A]/50 text-xs font-medium">
                                <PName name={g.name} />
                              </button>
                            ));
                      }
                      // Tümü — iki grup başlıkla
                      const perdeItems = filteredGroups.filter(g => PERDE_SLUGS.has(g.slug));
                      const golgeItems = filteredGroups.filter(g => GOLGELENDIRME_SLUGS.has(g.slug));
                      const digerItems = filteredGroups.filter(g => !PERDE_SLUGS.has(g.slug) && !GOLGELENDIRME_SLUGS.has(g.slug));
                      const renderGroup = (cat, items) => items.length === 0 ? null : (
                        <div key={cat.key}>
                          <button data-testid={`quote-cat-header-${cat.key}`}
                            onClick={(e) => { e.stopPropagation(); setProdCategory(cat.key); setProductQuery(""); }}
                            className={`w-full flex items-center justify-between px-3 py-1.5 bg-slate-50 dark:bg-[#0e1a2e] border-b border-slate-100 dark:border-[#22304A] group`}>
                            <span className={`text-[10px] font-black uppercase tracking-wider ${cat.color}`}>{cat.label}</span>
                            <span className="text-[10px] text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">sadece göster →</span>
                          </button>
                          {items.map((g) => (
                            <button key={g.id} data-testid={`quote-sel-prod-${g.slug}`} onClick={() => openProductForm(g)}
                              className="w-full text-left px-3 py-2 hover:bg-[#E5322A]/5 border-b border-slate-100 dark:border-[#22304A]/50 text-xs font-medium">
                              <PName name={g.name} />
                            </button>
                          ))}
                        </div>
                      );
                      return (
                        <>
                          {renderGroup(PRODUCT_CATEGORIES[0], perdeItems)}
                          {renderGroup(PRODUCT_CATEGORIES[1], golgeItems)}
                          {digerItems.map((g) => (
                            <button key={g.id} data-testid={`quote-sel-prod-${g.slug}`} onClick={() => openProductForm(g)}
                              className="w-full text-left px-3 py-2 hover:bg-[#E5322A]/5 border-b border-slate-100 dark:border-[#22304A]/50 text-xs font-medium">
                              <PName name={g.name} />
                            </button>
                          ))}
                          {filteredGroups.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-500" data-testid="quote-product-empty">{tr("Eşleşen ürün grubu yok")}</div>}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </>
            )}
                </div>
              </div>
            </div>

            {draftItem && activeGroup && !productDrawerOpen && (
              <div className="mx-4 sm:mx-5 mt-4 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 flex items-center gap-2" data-testid="quote-product-draft-card">
                <div className="min-w-0 flex-1">
                  <div className="text-[10px] font-black uppercase tracking-wider text-amber-600 dark:text-amber-300">Taslak</div>
                  <div className="text-xs font-bold truncate" data-testid="quote-product-draft-name"><PName name={draftItem.product || activeGroup.name} /></div>
                </div>
                <button onClick={() => { setStep("product"); setProductDrawerOpen(true); }} className="zen-btn-ghost !text-[11px] !py-1.5" data-testid="quote-product-draft-continue-btn">Devam Et</button>
                <button onClick={deleteDraftProduct} className="p-2 rounded-md text-rose-500 hover:bg-rose-500/10" data-testid="quote-product-draft-delete-btn"><Trash2 className="w-4 h-4" /></button>
              </div>
            )}

            {/* Özet şeridi (05.11): kalem sayısı · toplam m² · sepette arama */}
            <div className="shrink-0 px-4 sm:px-5 py-2 border-b border-slate-200 dark:border-[#22304A] flex items-center gap-2" data-testid="quote-cart-summary">
              <span className="text-[11px] font-bold px-2.5 py-1.5 rounded-full bg-slate-100 dark:bg-[#1A2340] text-slate-600 dark:text-slate-300 whitespace-nowrap">{items.length} kalem</span>
              <span className="text-[11px] font-bold px-2.5 py-1.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300 whitespace-nowrap">{cartTotalM2.toLocaleString("tr-TR", { maximumFractionDigits: 2 })} m²</span>
              <input
                data-testid="quote-cart-search"
                className="zen-input !py-1 !text-xs flex-1 min-w-0"
                placeholder="Sepette ara (ürün / oda)…"
                value={cartQuery}
                onChange={(e) => setCartQuery(e.target.value)} />
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5" data-testid="quote-products-list-scroll">
              {/* Gümrük Masrafı Uyarısı — yurt dışı müşterilerde */}
              {showCustomsBanner && items.length > 0 && (
                <div className="mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-300 dark:border-amber-500/40 text-amber-800 dark:text-amber-300" data-testid="customs-fee-banner">
                  <AlertTriangle className="w-4 h-4 shrink-0 text-amber-500" />
                  <span className="text-xs font-semibold flex-1 min-w-0">Yurt dışı müşteri — Gümrük masrafı?</span>
                  <input
                    type="number"
                    value={customsFeeAmount}
                    onChange={e => setCustomsFeeAmount(e.target.value)}
                    className="w-14 text-xs border border-amber-300 dark:border-amber-600 rounded px-1.5 py-0.5 bg-white dark:bg-[#0C1220] text-slate-900 dark:text-white text-center shrink-0"
                    data-testid="customs-fee-amount-input"
                  />
                  <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 shrink-0">EUR</span>
                  <button
                    onClick={addCustomsFee}
                    className="text-xs font-bold px-2 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white transition shrink-0"
                    data-testid="customs-fee-add-btn">
                    Ekle
                  </button>
                  <button
                    onClick={() => setCustomsDismissed(true)}
                    className="text-[10px] text-amber-500 hover:text-amber-700 dark:hover:text-amber-200 transition shrink-0"
                    data-testid="customs-fee-dismiss-btn">
                    Geçsem
                  </button>
                </div>
              )}
              {items.length === 0 ? (
                <div className="h-full min-h-[240px] rounded-md border border-dashed zen-hc-panel flex flex-col items-center justify-center text-center px-6" data-testid="quote-products-empty-state">
                  <Package className="w-9 h-9 text-slate-300 mb-3" />
                  <div className="text-sm font-bold text-slate-700 dark:text-slate-200">Henüz ürün eklenmedi</div>
                  <div className="text-xs text-slate-400 mt-1">Ürün Ekle ile mevcut ürün grubu listesinden başlayın.</div>
                </div>
              ) : (
                <div className="p-1" data-testid="quote-products-list">
                  <table className="w-full border-collapse" style={{ tableLayout: "fixed" }}>
                    <thead>
                      <tr className="text-[9px] font-black uppercase tracking-widest text-slate-400 border-b border-slate-200 dark:border-[#22304A]">
                        <th className="text-left px-1 pb-1" style={{ width: "4%" }}>#</th>
                        <th className="text-left px-1 pb-1" style={{ width: "17%" }}>Ürün</th>
                        <th className="text-right px-1 pb-1" style={{ width: "7%" }}>EN</th>
                        <th className="text-right px-1 pb-1" style={{ width: "7%" }}>BOY</th>
                        <th className="text-left px-1 pb-1" style={{ width: "12%" }}>Kumaş</th>
                        <th className="text-right px-1 pb-1" style={{ width: "13%" }}>Birim</th>
                        <th className="text-right px-1 pb-1" style={{ width: "15%" }}>Toplam</th>
                        {isAdmin && <th className="text-right px-1 pb-1" style={{ width: "8%" }}>Maliyet</th>}
                        {isAdmin && <th className="text-right px-1 pb-1" style={{ width: "5%" }}>Kâr</th>}
                        <th className="px-1 pb-1" style={{ width: "12%" }}></th>
                      </tr>
                    </thead>
                    <tbody>
                  {visibleGroups.map(({ g: grp, gi }) => {
                      const isDragging = draggingGi === gi;
                      const isFreshRow = Boolean(fresh) && fresh.index === grp.i &&
                        freshHighlightClass(fresh.at, Date.now()) === "fresh";
                      /* Montaj / Dikiş / Manuel — ince tek satır */
                      if (grp.type === "service") {
                        const { i, it } = grp;
                        return (
                          <tr key={i}
                            draggable
                            onDragStart={onGroupDragStart(gi)}
                            onDragOver={onGroupDragOver(gi)}
                            onDrop={onGroupDrop}
                            onDragEnd={onGroupDragEnd}
                            className={`group border-b border-slate-100 dark:border-[#22304A]/40 hover:bg-slate-500/[0.04] transition-colors cursor-grab active:cursor-grabbing ${isDragging ? "opacity-40 bg-[#E5322A]/10" : isFreshRow ? "bg-emerald-50/80 dark:bg-emerald-500/10" : ""}`}
                            data-testid={`quote-cart-item-${i}`}>
                            <td className="px-1.5 py-1.5">
                              <div className="flex items-center gap-0.5">
                                <GripVertical className="w-3 h-3 text-slate-300 dark:text-slate-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                                <span className="w-[16px] text-[10px] font-bold text-slate-500 dark:text-slate-400 text-right select-none" data-testid={`quote-cart-item-index-${i}`}>{gi + 1}</span>
                              </div>
                            </td>
                            <td className="px-1.5 py-1.5 min-w-0" colSpan={4}>
                              <span className="text-xs text-slate-500 dark:text-slate-400">→ <PName name={it.product} /></span>
                            </td>
                            <td className="px-1.5 py-1.5 text-right text-[11px] zen-mono text-slate-500">{it.qty > 0 ? Math.round((it.lineTotal / it.qty) * 100) / 100 : it.lineTotal} {it.currency}</td>
                            <td className="px-1.5 py-1.5 text-right text-[13px] zen-mono font-bold text-slate-700 dark:text-slate-200">{fmt(it.lineTotal, it.currency)}</td>
                            {isAdmin && <td className="px-1.5 py-1.5"></td>}
                            {isAdmin && <td className="px-1.5 py-1.5"></td>}
                            <td className="px-1.5 py-1.5 text-right">
                                      <span className="inline-flex items-center gap-0.5 mr-1 align-middle">
                                        <button onClick={() => changeQty(i, -1)} data-testid={`quote-cart-qty-dec-${i}`} title="Adet azalt" className="w-5 h-5 leading-none rounded bg-slate-100 dark:bg-[#1A2340] text-slate-500 dark:text-slate-300 text-[11px] hover:bg-slate-200 dark:hover:bg-[#22304A] transition-colors">−</button>
                                        <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 min-w-[18px] text-center select-none">{it.qty || 1}</span>
                                        <button onClick={() => changeQty(i, 1)} data-testid={`quote-cart-qty-inc-${i}`} title="Adet artır" className="w-5 h-5 leading-none rounded bg-slate-100 dark:bg-[#1A2340] text-slate-500 dark:text-slate-300 text-[11px] hover:bg-slate-200 dark:hover:bg-[#22304A] transition-colors">+</button>
                                      </span>
                                      <button onClick={() => duplicateItemAt(i)} data-testid={`quote-cart-duplicate-${i}`} title="Çoğalt (Ctrl+D)" className="opacity-60 group-hover:opacity-100 p-0.5 text-slate-400 hover:bg-slate-500/10 rounded transition-all">
                                        <Copy className="w-3 h-3" />
                                      </button>
                                      <button onClick={() => removeItem(i)} data-testid={`quote-row-delete-${i}`} className="opacity-60 group-hover:opacity-100 p-0.5 text-rose-400 hover:bg-rose-500/10 rounded transition-all">
                                <Trash2 className="w-3 h-3" />
                              </button>
                            </td>
                          </tr>
                        );
                      }

                      /* Ana ürün — chip'ler + altta ait aksesuarlar */
                      const { i, it, subs } = grp;
                      const _blank = (v) => !v || v === "--Boş Bırakıldı--" || v === "Yok" || v === "Hayır" || v === "Standart";
                      const isGlass = ["giyotin_cam","cam_tavan","surme_cam_sistemi"].includes(it.groupSlug);
                      const chips = [
                        it.widthCm  > 0 && { lbl: "EN",       val: `${it.widthCm}` },
                        it.heightCm > 0 && { lbl: "BOY",      val: `${it.heightCm}` },
                        // Cam ürünler: cam tipi + RAL (tentRalCode VEYA ralCode)
                        isGlass && it.matrixVariant && { lbl: "CAM TİPİ", val: it.matrixVariant },
                        isGlass && (it.tentRalCode || it.ralCode) && { lbl: "RAL", val: it.tentRalCode || it.ralCode },
                        // Diğer ürünler: kumaş
                        !isGlass && (it.fabric || it.ralCode || it.slatColorCode) && { lbl: it.fabric2 ? "ÖN KUMAŞ" : "KUMAŞ", val: it.fabric || it.ralCode || it.slatColorCode },
                        !isGlass && it.fabric2 && { lbl: "ARKA KUMAŞ", val: it.fabric2 },
                        it.kasaTipi && { lbl: "KASA TİPİ", val: it.kasaTipi },
                        it.controlSide  && { lbl: "YÖN",      val: it.controlSide },
                        !_blank(it.kasa)       && { lbl: "KASA",    val: it.kasa },
                        !_blank(it.kasaRengi)  && { lbl: "RENGİ",   val: it.kasaRengi },
                        !_blank(it.kasaKumas)  && { lbl: "K.KUMAŞ", val: it.kasaKumas },
                        !_blank(it.zincir)     && { lbl: "ZİNCİR",  val: it.zincir },
                        !_blank(it.mekanizma)  && { lbl: "MEK.",    val: it.mekanizma },
                        !_blank(it.altCita)    && { lbl: "ALT ÇITA", val: it.altCita },
                        !_blank(it.etekModel)  && { lbl: "ETEK",    val: it.etekModel },
                        !_blank(it.yanKanal)   && it.yanKanal === "Var" && { lbl: "YAN KANAL", val: "Var" },
                        !_blank(it.jumboKaset) && { lbl: "JUMBO",   val: it.jumboKaset },
                        !_blank(it.reduktor)   && it.reduktor === "Var" && { lbl: "REDÜKTÖR", val: "Var" },
                        (it.qty > 1)           && { lbl: "ADET",    val: `${it.qty} ${it.unit || "ad"}` },
                      ].filter(Boolean);
                      const coreLabels = ["EN", "BOY", "KUMAŞ", "ÖN KUMAŞ", "ARKA KUMAŞ", "CAM TİPİ", "RAL"];
                      const extraChips = chips.filter(c => !coreLabels.includes(c.lbl));
                      const detayVal = [
                        ...(isGlass ? [it.matrixVariant, it.tentRalCode || it.ralCode] : [it.fabric || it.ralCode || it.slatColorCode]),
                        it.fabric2,
                      ].filter(Boolean).join(" · ");
                      return (
                        <React.Fragment key={gi}>
                          <tr
                            draggable
                            onDragStart={onGroupDragStart(gi)}
                            onDragOver={onGroupDragOver(gi)}
                            onDrop={onGroupDrop}
                            onDragEnd={onGroupDragEnd}
                            className={`group border-b border-slate-100 dark:border-[#22304A]/40 hover:bg-[#E5322A]/[0.04] transition-colors cursor-grab active:cursor-grabbing ${isDragging ? "opacity-40 bg-[#E5322A]/10" : isFreshRow ? "bg-emerald-50/80 dark:bg-emerald-500/10" : ""}`}
                            data-testid={`quote-cart-item-${i}`}>
                            <td className="px-1.5 py-1.5 align-middle">
                              <div className="flex items-center gap-0.5">
                                <GripVertical className="w-2.5 h-2.5 text-slate-300 dark:text-slate-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`quote-drag-handle-${gi}`} />
                                <span className="w-[19px] h-[19px] rounded-full bg-[#E5322A]/10 text-[10px] font-black text-[#E5322A] flex items-center justify-center select-none" data-testid={`quote-cart-item-index-${i}`}>{gi + 1}</span>
                              </div>
                            </td>
                            <td className="px-1.5 py-1.5 min-w-0">
                              <button onClick={() => editItem(i)} className="w-full text-left truncate" data-testid={`quote-cart-edit-${i}`} title={`${it.product}${it.roomName ? ` — ${it.roomName}` : ""}`}>
                                <span className="text-[13px] font-bold text-slate-900 dark:text-white hover:text-[#E5322A] transition-colors">
                                  <PName name={it.product} />
                                </span>
                                {(() => {
                                  const extras = [
                                    it.motor && it.motorLabel ? it.motorLabel : null,
                                  ].filter(Boolean);
                                  return extras.length > 0 ? (
                                    <span className="ml-1 text-[10px] font-normal text-slate-500 dark:text-slate-400 whitespace-nowrap">
                                      · {extras.join(" · ")}
                                    </span>
                                  ) : null;
                                })()}
                              </button>
                            </td>
                            <td className="px-1.5 py-1.5 text-right">
                              <button onClick={() => editItem(i)} title="EN — düzenlemek için tıklayın" className="w-full text-right text-[13px] font-bold zen-mono text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded px-1.5 py-[2px] hover:border-[#E5322A]/60 hover:text-[#E5322A] transition-colors">
                                {it.widthCm > 0 ? it.widthCm : "—"}
                              </button>
                            </td>
                            <td className="px-1.5 py-1.5 text-right">
                              <button onClick={() => editItem(i)} title="BOY — düzenlemek için tıklayın" className="w-full text-right text-[13px] font-bold zen-mono text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20 rounded px-1.5 py-[2px] hover:border-[#E5322A]/60 hover:text-[#E5322A] transition-colors">
                                {it.heightCm > 0 ? it.heightCm : "—"}
                              </button>
                            </td>
                            <td className="px-1.5 py-1.5 min-w-0">
                              <button onClick={() => editItem(i)} data-testid={`quote-cart-meta-${i}`} title={`${detayVal || "Kumaş/Detay"} — düzenlemek için tıklayın`} className="w-full text-left text-[11px] text-slate-600 dark:text-slate-300 truncate border border-slate-200/70 dark:border-[#22304A]/70 rounded px-1 py-[1px] hover:border-[#E5322A]/60 hover:text-[#E5322A] transition-colors">
                                {detayVal || <span className="opacity-40">—</span>}
                              </button>
                            </td>
                            {(() => {
                              // Zip ürünleri her zaman kendi para biriminde gösterilir
                              const isZip = it.groupSlug?.includes("zip") || it.groupSlug?.includes("sky");
                              const isM2Item = it.pricingMode !== "matrix" && it.computedM2 > 0;
                              const showNative = isZip || targetCur === it.currency;
                              const dispTotal = showNative
                                ? it.lineTotal
                                : convertPrice(it.lineTotal, it.currency, targetCur, effectiveRates, "revenue");
                              const dispCur = showNative ? it.currency : targetCur;
                              const birimVal = isM2Item
                                ? (dispTotal / (Number(it.computedM2) * Math.max(1, Number(it.qty) || 1)))
                                : (it.qty > 0 ? dispTotal / it.qty : dispTotal);
                              const costDisp = Number(itemCosts[i] || 0) > 0 ? convertPrice(Number(itemCosts[i]), "TL", dispCur, effectiveRates) : 0;
                              const karPct = costDisp > 0 ? Math.round((dispTotal - costDisp) / costDisp * 100) : null;
                              return (
                                <>
                                  <td className="px-1.5 py-1.5 text-right whitespace-nowrap">
                                    {canEditPrice && qEditItemPriceIdx === i ? (
                                      <span className="inline-flex items-center gap-0.5">
                                        <input
                                          data-testid={`qb-item-price-input-${i}`}
                                          className="zen-input !py-0 !text-[11px] w-[68px] text-right font-bold"
                                          value={qEditItemM2Val}
                                          onChange={e => {
                                            const v = e.target.value; setQEditItemM2Val(v);
                                            const n = parseFloat(v.replace(",", "."));
                                            if (isFinite(n) && n >= 0) {
                                              // Birim fiyat GÖRÜNEN para biriminde girilir → kalem toplamına (kendi
                                              // biriminde) çevrilir. Kuruş yuvarlaması YOK (31.10.2026): yuvarlanırsa
                                              // gösterilen birim fiyat küsuratlanır (1300 → 1300,02).
                                              const totNative = manualLineTotalFromUnit(it, n, dispCur, effectiveRates);
                                              applyItemPriceLive(i, totNative);
                                              setQEditItemPriceVal(String(totNative));
                                            }
                                          }}
                                          onBlur={() => commitItemPriceQB(i)}
                                          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); commitItemPriceQB(i); } if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); revertItemPriceQB(i); } }}
                                          autoFocus
                                        />
                                        <span className="text-[9px] text-slate-400">{isM2Item ? `/${qtyUnitLabel(isTulFonItem(it))}` : "/ad"}</span>
                                      </span>
                                    ) : canEditPrice ? (
                                      <button
                                        data-testid={`qb-item-price-edit-${i}`}
                                        className="zen-mono text-[11px] font-semibold text-slate-700 dark:text-slate-200 border border-dashed border-slate-400/80 dark:border-slate-500/80 rounded px-2 py-[2px] hover:border-[#E5322A] hover:text-[#E5322A] hover:bg-[#E5322A]/5 transition-colors cursor-text"
                                        title={`Birim fiyatı düzenlemek için tıklayın (${it.currency}) · Enter kaydet · ESC vazgeç`}
                                        onClick={e => {
                                          e.stopPropagation();
                                          setQEditItemPriceIdx(i);
                                          setQEditItemPriceVal(String(Math.round(it.lineTotal * 100) / 100));
                                          // Input GÖRÜNEN birim fiyatla açılır (kullanıcının gördüğü değer)
                                          setQEditItemM2Val(String(Math.round(birimVal * 100) / 100));
                                          setQEditItemPriceOrig({ lineTotal: it.lineTotal, unitPrice: it.unitPrice, isManualPrice: it.isManualPrice, listLineTotal: it.listLineTotal });
                                        }}
                                      >
                                        ✎ {fmt(birimVal, dispCur)}{isM2Item ? `/${qtyUnitLabel(isTulFonItem(it))}` : ""}
                                      </button>
                                    ) : (
                                      <span className="zen-mono text-[10px] text-slate-500 inline-block border border-transparent rounded px-1.5 py-[1px]">{fmt(birimVal, dispCur)}{isM2Item ? `/${qtyUnitLabel(isTulFonItem(it))}` : ""}</span>
                                    )}
                                  </td>
                                  <td className="px-1.5 py-1.5 text-right whitespace-nowrap">
                                    <span className="zen-mono text-[11px] font-bold">{fmt(dispTotal, dispCur)}</span>
                                    {it.isManualPrice && <span className="ml-1 text-[9px] font-bold text-emerald-600 dark:text-emerald-400">MANUEL</span>}
                                    {isAdmin && it.isManualPrice && Number(itemCosts[i] || 0) > 0 && Number(it.lineTotal) < convertPrice(Number(itemCosts[i]) * 1.35, "TL", it.currency || "TL", effectiveRates, "cost") && (
                                      <span className="ml-1 text-[9px] font-bold text-amber-500 dark:text-amber-400" title="Fiyat maliyet+%35 tabanının altında — minimum kâr garantisi yok">⚠ TABAN ALTI</span>
                                    )}
                                    {isAdmin && !it.isManualPrice && it.marginPriced > 0 && !it.dealerRuleLabel && !(Number(it.discountPercent) > 0) && (
                                      <span className="ml-1 w-1.5 h-1.5 inline-block rounded-full bg-sky-500 align-middle" title={`Kâr Oranı %${it.marginPriced} ile maliyet üzerinden otomatik fiyatlandı — oran değişirse bu kalem de değişir`} />
                                    )}
                                    {it.dealerRuleLabel && <span className="ml-1 text-[9px] font-bold text-violet-600 dark:text-violet-400" title={it.dealerRuleLabel}>ÖZEL</span>}
                                  </td>
                                  {isAdmin && <td className="px-1.5 py-1.5 text-right text-[10px] zen-mono text-rose-500">{costDisp > 0 ? fmt(costDisp, dispCur) : "—"}</td>}
                                  {isAdmin && (() => {
                                    const isMarj = !it.isManualPrice && it.marginPriced > 0 && !it.dealerRuleLabel && !(Number(it.discountPercent) > 0);
                                    return (
                                      <td
                                        className={`px-1.5 py-1.5 text-right text-[10px] font-bold ${isMarj ? "text-sky-500" : "text-emerald-600 dark:text-emerald-400"}`}
                                        title={costDisp > 0 ? `Kâr tutarı: ${fmt(dispTotal - costDisp, dispCur)}${isMarj ? ` · Kâr Oranı %${it.marginPriced} ile otomatik fiyatlandı` : it.isManualPrice ? " · Manuel fiyat — sabit" : it.dealerRuleLabel ? ` · ${it.dealerRuleLabel}` : ""}` : undefined}
                                      >
                                        {karPct != null ? `%${karPct}` : "—"}
                                      </td>
                                    );
                                  })()}
                                  <td className="px-1.5 py-1.5 text-right align-middle">
                                    <div className="flex items-center justify-end gap-0.5">
                                      {it.groupSlug === "tul_fon_perde" && <button onClick={() => editItem(i)} data-testid={`quote-tailor-edit-${i}`} title="Oda ve terzi detaylarını düzenle" className="px-1 text-[9px] font-semibold text-sky-600 dark:text-sky-300 hover:bg-sky-500/10 rounded">Terzi</button>}
                                              <span className="inline-flex items-center gap-0.5 mr-1 align-middle">
                                        <button onClick={() => changeQty(i, -1)} data-testid={`quote-cart-qty-dec-${i}`} title="Adet azalt" className="w-5 h-5 leading-none rounded bg-slate-100 dark:bg-[#1A2340] text-slate-500 dark:text-slate-300 text-[11px] hover:bg-slate-200 dark:hover:bg-[#22304A] transition-colors">−</button>
                                        <span className="text-[11px] font-bold text-slate-500 dark:text-slate-400 min-w-[18px] text-center select-none">{it.qty || 1}</span>
                                        <button onClick={() => changeQty(i, 1)} data-testid={`quote-cart-qty-inc-${i}`} title="Adet artır" className="w-5 h-5 leading-none rounded bg-slate-100 dark:bg-[#1A2340] text-slate-500 dark:text-slate-300 text-[11px] hover:bg-slate-200 dark:hover:bg-[#22304A] transition-colors">+</button>
                                      </span>
                                      <button onClick={() => duplicateItemAt(i)} data-testid={`quote-cart-duplicate-${i}`} title="Çoğalt (Ctrl+D)" className="opacity-60 group-hover:opacity-100 p-0.5 text-slate-400 hover:bg-slate-500/10 rounded transition-all">
                                        <Copy className="w-3 h-3" />
                                      </button>
                                      <button onClick={() => removeItem(i)} data-testid={`quote-row-delete-${i}`} className="opacity-60 group-hover:opacity-100 p-0.5 text-rose-400 hover:bg-rose-500/10 rounded transition-all">
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    </div>
                                  </td>
                                </>
                              );
                            })()}
                          </tr>
                          {/* Bağlı aksesuarlar (Kumanda / Sensör / Stor aksesuarı) —
                              her biri ayrı satır + kendi silme düğmesi. Önceden sadece
                              ana ürün adının yanında metin olarak görünüyordu ve
                              silinemiyordu (örn. 96 adet Kumanda). */}
                          {subs.map(({ i: si, it: sit }) => (
                            <tr key={`sub-${si}`} className="group border-b border-l-2 border-l-slate-300 dark:border-l-slate-600 border-slate-100 dark:border-[#22304A]/40 bg-slate-50/60 dark:bg-[#0C1220]/40" data-testid={`quote-cart-sub-${si}`}>
                              <td className="px-1.5 py-1"></td>
                              <td colSpan={4} className="px-1 pl-3 py-[2px] min-w-0">
                                <span className="inline-flex items-center gap-1 min-w-0">
                                  <span className="text-[10px] text-slate-400">↳</span>
                                  <span className="text-[11px] font-medium text-slate-600 dark:text-slate-300 truncate"><PName name={sit.product} /></span>
                                </span>
                              </td>
                              <td className="px-1.5 py-1 text-right text-[11px] zen-mono text-slate-500 whitespace-nowrap">
                                {Number(sit.qty || 1)} {sit.unit === "m²" && !(Number(sit.computedM2) > 0) ? "Ad" : (sit.unit || "Ad")}
                              </td>
                              <td className="px-1.5 py-1 text-right text-[12px] zen-mono font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                {fmt(sit.lineTotal, sit.currency)}
                              </td>
                              {isAdmin && <td className="px-1.5 py-1"></td>}
                              {isAdmin && <td className="px-1.5 py-1"></td>}
                              <td className="px-1.5 py-1 text-right">
                                <button onClick={() => removeItems([si])} data-testid={`quote-sub-delete-${si}`} title="Aksesuarı sil" aria-label="Aksesuarı sil" className="opacity-60 hover:opacity-100 p-0.5 text-rose-400 hover:bg-rose-500/10 rounded transition-all">
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </td>
                            </tr>
                          ))}
                          {(extraChips.length > 0 || Number(it.motorTotalInCur) > 0) && (
                            <tr key={`det-${gi}`} className="border-b border-slate-100 dark:border-[#22304A]/40">
                              <td></td>
                              <td colSpan={isAdmin ? 9 : 7} className="px-1 pb-1 pt-0 text-[9px] text-slate-400 dark:text-slate-500 truncate">
                                <span title={extraChips.map(c => `${c.lbl}: ${c.val}`).join(" · ")}>
                                  {extraChips.map(c => `${c.lbl}: ${c.val}`).join(" · ")}
                                  {Number(it.motorTotalInCur) > 0 && (
                                    <span className="ml-2 text-amber-600 dark:text-amber-400 font-semibold" data-testid={`qb-item-motor-${i}`}>
                                      ⚙ Motor{it.motorQty ? ` ${it.motorQty}×` : ""}{it.motorLabel ? ` · ${it.motorLabel}` : ""} {fmt(it.motorTotalInCur, it.currency)} (dahil)
                                    </span>
                                  )}
                                </span>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                    {/* Canlı önizleme (05.11): föy açıkken yazılan kalem, fiyatıyla
                        birlikte tablonun SONUNA kesik çerçeveli satır olarak düşer
                        (giriş sırası kuralı). Yalnız YENİ girişte — düzenlemede
                        gerçek satır zaten listede (hayalet satır yok). */}
                    {showPreviewRow({ draftItem, activeGroup, drawerOpen: productDrawerOpen, editingIndex: editingItemIndex }) && (() => {
                      const w = Number(draftItem.widthCm) || 0;
                      const h = Number(draftItem.heightCm) || 0;
                      const qty = Number(draftItem.qty) || 1;
                      if (!w && !h) return null;
                      let prevTotal = 0;
                      let prevCur = targetCur;
                      try {
                        const storExtra = activeGroup.slug === STOR_SISTEM_SLUG
                          ? { ...draftItem, mekanizmaPrice: (findPriceItem(priceItems, (RECIPE_CODES.mekanizma[draftItem.sistem === "motorlu" ? "motorlu" : "manuel"] || {})[draftItem.kasaTipi]) || {}).basePrice }
                          : draftItem;
                        const calc = computeLineTotal(activeGroup, w, h, qty, storExtra, matrices, rates);
                        prevTotal = Number(calc.total) || 0;
                        prevCur = calc.currency || targetCur;
                        if (draftItem.motor && Number(draftItem.motorPrice) > 0) {
                          const mCur = draftItem.motorCurrency || prevCur;
                          const mUnit = mCur === prevCur ? Number(draftItem.motorPrice) : convertPrice(Number(draftItem.motorPrice), mCur, prevCur, rates);
                          prevTotal += mUnit * qty;
                        }
                      } catch { /* fiyat hesaplanamıyorsa satır yine görünür */ }
                      return (
                        <tr data-testid="quote-cart-preview" className="bg-slate-50/70 dark:bg-slate-800/30" style={{ outline: "1px dashed #94a3b8", outlineOffset: "-1px" }}>
                          <td className="px-1 py-1 text-[10px] font-bold text-slate-400">+</td>
                          <td className="px-1 py-1 min-w-0">
                            <div className="flex items-center gap-1 min-w-0">
                              <span className="text-[12px] font-bold text-slate-500 dark:text-slate-300 truncate"><PName name={draftItem.product || activeGroup.name} /></span>
                              <span className="text-[7px] font-black uppercase tracking-wider bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-200 rounded px-1 py-[1px] shrink-0">ÖNİZLEME</span>
                            </div>
                          </td>
                          <td className="px-1 py-1 text-right text-[12px] zen-mono text-slate-400">{w || "-"}</td>
                          <td className="px-1 py-1 text-right text-[12px] zen-mono text-slate-400">{h || "-"}</td>
                          <td className="px-1 py-1 text-[9px] text-slate-400 truncate">{draftItem.fabric || draftItem.ralCode || ""}</td>
                          <td className="px-1 py-1 text-right text-[9px] text-slate-400">{qty > 1 ? `${qty} adet` : ""}</td>
                          <td className="px-1 py-1 text-right text-[12px] zen-mono font-bold text-slate-500 dark:text-slate-300">{fmt(prevTotal, prevCur)}</td>
                          {isAdmin && <td className="px-1 py-1"></td>}
                          {isAdmin && <td className="px-1 py-1"></td>}
                          <td className="px-1 py-1"></td>
                        </tr>
                      );
                    })()}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Sol Panel Toplam Footer */}
            {items.length > 0 && (
              <div className="shrink-0 border-t border-slate-200 dark:border-[#22304A] px-4 py-2.5 bg-white/60 dark:bg-[#0C1220]/90" data-testid="qb-left-totals-footer">
                <div className="flex justify-between items-center text-[13px] text-slate-500 dark:text-slate-400 mb-1">
                  <span>Ara Toplam</span>
                  <span className="zen-mono font-semibold" data-testid="qb-left-subtotal">{fmt(subtotal, targetCur)}</span>
                </div>
                {!vatExempt && autoVatAmt > 0 && (
                  <div className="flex justify-between items-center text-[13px] text-slate-500 dark:text-slate-400 mb-1.5">
                    <span>KDV</span>
                    <span className="zen-mono" data-testid="qb-left-kdv">{fmt(autoVatAmt, targetCur)}</span>
                  </div>
                )}
                {vatExempt && (
                  <div className="flex justify-between items-center text-[13px] text-emerald-600 dark:text-emerald-400 mb-1.5">
                    <span>KDV</span>
                    <span className="zen-mono font-semibold">%0 İstisna</span>
                  </div>
                )}
                <div className="flex justify-between items-center pt-1.5 border-t border-slate-200 dark:border-[#22304A]">
                  <span className="text-[13px] font-bold text-slate-700 dark:text-slate-200">Genel Toplam</span>
                  {isAdmin ? (
                    <div className="flex flex-col items-end gap-0.5">
                      <input
                        data-testid="qb-left-total-input"
                        className="zen-input !py-0.5 !text-sm font-extrabold w-36 text-right !text-[#E5322A] dark:!text-[#ff6b6b]"
                        placeholder={fmt(effectiveTotalQB, targetCur)}
                        value={qbLeftTotalStr}
                        onChange={e => setQbLeftTotalStr(e.target.value)}
                        onBlur={() => { if (qbLeftTotalStr.trim()) { redistributeByTotal(qbLeftTotalStr); setQbLeftTotalStr(""); } }}
                        onKeyDown={e => {
                          if (e.key === "Enter") { redistributeByTotal(qbLeftTotalStr); setQbLeftTotalStr(""); e.currentTarget.blur(); }
                          if (e.key === "Escape") { setQbLeftTotalStr(""); e.currentTarget.blur(); }
                        }}
                        title="Yeni Genel Toplam girin → Enter → Kalem tutarları orantılı güncellenir"
                      />
                      {qbLeftTotalStr.trim() && (
                        <span className="text-[10px] text-slate-400">↵ Enter ile kalemler güncellenir</span>
                      )}
                    </div>
                  ) : (
                    <span className="zen-mono text-sm font-extrabold text-[#E5322A]" data-testid="qb-left-grand-total">{fmt(effectiveTotalQB, targetCur)}</span>
                  )}
                </div>
              </div>
            )}
          </aside>

          <main className={`min-h-0 overflow-y-auto p-5 sm:p-8 space-y-6 zen-hc-workspace ${(!rightPanelOpen || productDrawerOpen) ? 'hidden' : ''}`} data-testid="quote-details-panel">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">{saveAsOrder ? "Sipariş Düzenle" : "Teklif Hazırla"}</div>
                <h2 className="text-2xl sm:text-3xl font-black text-slate-950 dark:text-white mt-1" data-testid="quote-details-title">
                  {isRevMode ? `Revize Ediliyor #${editingQuote.quoteNo}` : editingQuote?._new ? `CRM'den Yeni Sipariş${editingQuote._leadTitle ? ` · ${editingQuote._leadTitle}` : ""}` : isEdit ? (saveAsOrder ? `Sipariş #${editingQuote.orderNo || editingQuote.id?.slice(-6)?.toUpperCase() || ""}` : `Teklif #${editingQuote.quoteNo}`) : (saveAsOrder ? "Yeni Sipariş" : "Yeni Teklif")}
                </h2>
              </div>
              <button onClick={onClose} data-testid="quote-builder-close-btn" className="p-2 hover:bg-slate-100 dark:hover:bg-[#22304A] rounded-md transition-colors"><X className="w-5 h-5" /></button>
            </div>

            <section className="space-y-4" data-testid="quote-customer-info-section">
              <div className="grid md:grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{tr("Müşteri")}</label>
            <input
              data-testid="quote-customer-input"
              className="zen-input"
              placeholder={onlyRegisteredCustomers ? tr("Kayıtlı müşteri seçin…") : tr("Müşteri seç veya yeni müşteri adı yaz…")}
              value={customerId ? (customer?.name || editingQuote?.customerName || "") : customerQuery}
              onFocus={() => setShowCustDropdown(true)}
              onChange={(e) => { setCustomerId(""); setCustomerQuery(e.target.value); setShowCustDropdown(true); }}
            />
            {showCustDropdown && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowCustDropdown(false)} />
                <div className="absolute z-40 top-full left-0 right-0 mt-1 zen-panel max-h-72 overflow-y-auto shadow-xl" data-testid="quote-customer-dropdown">
                  {filteredCustomers.map((c) => (
                    <button key={c.id} onClick={() => selectCustomer(c)} data-testid={`quote-sel-cust-${c.id}`} className="w-full text-left px-3 py-2 hover:bg-[#E5322A]/5 border-b border-slate-100 dark:border-[#22304A]/50 text-xs">
                      <span className="font-semibold">{c.name}</span> <span className="text-slate-400">· {c.currency} · {c.type}</span>
                    </button>
                  ))}
                  {filteredCustomers.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-500">{tr("Eşleşen müşteri yok")}</div>}
                  {customerQuery.trim() && !onlyRegisteredCustomers && (
                    <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 border-t border-indigo-200 dark:border-indigo-500/20">
                      <div className="text-[11px] font-semibold mb-1.5">"{customerQuery.trim()}" için yeni Perakende Müşteri oluştur</div>
                      <div className="flex flex-col gap-2">
                        <input data-testid="quote-new-cust-phone" placeholder={tr("Telefon (opsiyonel)")} className="zen-input !py-1.5 !text-xs" value={newCustPhone} onChange={(e) => setNewCustPhone(e.target.value)} onClick={(e) => e.stopPropagation()} />
                        <input data-testid="quote-new-cust-address" placeholder={tr("Adres (opsiyonel)")} className="zen-input !py-1.5 !text-xs" value={newCustAddress} onChange={(e) => setNewCustAddress(e.target.value)} onClick={(e) => e.stopPropagation()} />
                        <button data-testid="quote-add-new-cust-btn" onClick={(e) => { e.stopPropagation(); createInlineCustomer(); }} className="zen-btn-primary !text-[11px] !py-1.5">{tr("Ekle")}</button>
                      </div>
                    </div>
                  )}
                  {customerQuery.trim() && onlyRegisteredCustomers && filteredCustomers.length === 0 && (
                    <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border-t border-amber-200 dark:border-amber-800/40">
                      <div className="text-[11px] text-amber-700 dark:text-amber-300">Bu isimde kayıtlı müşteri bulunamadı. Müşteriler sayfasından yeni müşteri ekleyebilirsiniz.</div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          {/* Teklif Para Birimi Dropdown */}
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Teklif Para Birimi</label>
            <select
              data-testid="quote-currency-select"
              className="zen-input"
              value={quoteCurrency}
              onChange={(e) => setQuoteCurrency(e.target.value)}
            >
              <option value="TL">₺ Türk Lirası (TL)</option>
              <option value="EUR">€ Euro (EUR)</option>
              <option value="USD">$ Dolar (USD)</option>
            </select>
            {/* Müşteri para birimi ile uyuşuyor — görsel onay */}
            {customerId && customer?.currency && (
              <div className={`text-[10px] mt-1 flex items-center gap-1 ${customer.currency.toUpperCase() === quoteCurrency ? "text-emerald-600 dark:text-emerald-400" : "text-amber-600 dark:text-amber-400"}`}>
                {customer.currency.toUpperCase() === quoteCurrency
                  ? <span>✓ Müşteri para birimiyle eşleşiyor ({customer.currency})</span>
                  : <span>⚠ Müşteri kaydı: {customer.currency} — seçilen: {quoteCurrency}</span>
                }
              </div>
            )}
            {quoteCurrency === "TL" && effectiveRates?.eurToTl > 0 && (
              <div className="text-[10px] text-slate-400 mt-1 flex items-center gap-1.5 flex-wrap">
                {editingRate ? (
                  <span className="flex items-center gap-1">
                    <span>1 EUR =</span>
                    <input
                      className="zen-input !py-0 !text-xs w-20 text-right font-bold"
                      value={editRateVal}
                      autoFocus
                      onChange={e => setEditRateVal(e.target.value)}
                      onBlur={() => { const v = parseFloat(editRateVal.replace(",",".")); if (v > 0) setCustomEurTl(v); setEditingRate(false); }}
                      onKeyDown={e => { if (e.key === "Enter") { const v = parseFloat(editRateVal.replace(",",".")); if (v > 0) setCustomEurTl(v); setEditingRate(false); } if (e.key === "Escape") setEditingRate(false); }}
                    />
                    <span>TL</span>
                  </span>
                ) : (
                  <>
                    <span>
                      1 EUR = <strong className={customEurTl ? "text-amber-400" : ""}>{Number(effectiveRates.eurToTl).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺</strong>
                      {customEurTl && <span className="ml-1 text-amber-400">(Manuel)</span>}
                    </span>
                    <button
                      className="text-slate-500 hover:text-indigo-400 transition-colors"
                      title="Kuru manuel gir"
                      onClick={() => { setEditRateVal(String(effectiveRates.eurToTl)); setEditingRate(true); }}
                    >✏</button>
                    {customEurTl && (
                      <button className="text-rose-400 hover:text-rose-300 transition-colors" title="TCMB kuruna dön" onClick={() => setCustomEurTl(null)}>✕</button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{tr("Proje / İş Adı")}</label>
            <input data-testid="quote-project-input" className="zen-input" value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder={tr("Opsiyonel")} />
          </div>
          <div>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">
              Geçerlilik Süresi (Gün)
            </label>
            <div className="relative">
              <input data-testid="quote-valid-days-input" type="number" min="1" max="365"
                className="zen-input pr-20"
                value={validDays}
                onChange={(e) => setValidDays(Number(e.target.value) || 15)} />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[11px] text-slate-400 pointer-events-none">
                {(() => { const d = new Date(); d.setDate(d.getDate() + (validDays || 15)); return d.toLocaleDateString("tr-TR", { day:"2-digit", month:"2-digit", year:"numeric" }); })()}
              </span>
            </div>
          </div>
              </div>
            </section>

            <section className="space-y-5" data-testid="quote-finance-section">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <h4 className="text-sm font-black" data-testid="quote-actions-title">Teklif Aksiyonları</h4>
                <div className="flex gap-2 flex-wrap">
              <button data-testid="add-manual-row-btn" onClick={() => { setManualRow({ product: "", unit: "adet", qty: 1, unitPrice: 0, currency: targetCur, kdvRate: 20 }); setShowManualRow(true); }} className="zen-btn-ghost !text-[11px] !py-1.5">
                <Plus className="w-3.5 h-3.5" /> Manuel Satır
              </button>
              <button data-testid="add-montaj-fee-btn" onClick={() => setShowMontajPrompt(true)} className="zen-btn-ghost !text-[11px] !py-1.5">
                <Plus className="w-3.5 h-3.5" /> Montaj Bedeli Ekle
              </button>
              <button data-testid="add-dikis-fee-btn" onClick={openDikisPrompt} className="zen-btn-ghost !text-[11px] !py-1.5">
                <Plus className="w-3.5 h-3.5" /> Dikiş Bedeli Ekle
              </button>
            </div>
          </div>

        {/* MALİYET & KÂR PANELİ — ortak bileşen (Sipariş Modalı ile birebir aynı) */}
        <MaliyetKarPanel
          items={items} itemCosts={itemCosts} subtotal={subtotal} targetCur={targetCur}
          rates={rates} fmt={fmt} karOrani={karOrani} setKarOrani={setKarOrani}
          isAdmin={isAdmin} testid="quote-kar-orani" docLabel="yalnızca size görünür"
        />

        {/* NOTLAR - Her zaman görünür */}
        <div className="mt-4">
          <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">{tr("Açıklama / Teklif Notları")}</label>
          <textarea data-testid="quote-notes-textarea" rows={items.length > 0 ? 6 : 4} className="zen-input" value={notes} onChange={(e) => { setNotes(e.target.value); setNotesTouched(true); }} placeholder={tr("Müşteri seçilince varsayılan notlar otomatik dolar…")} />
        </div>

        {/* TOPLAM - Ürün eklenince görünür */}
        {items.length > 0 && (
          <div className="mt-4 zen-panel p-5 space-y-2 bg-slate-50 dark:bg-[#0C1220]">
            {customer?.vatExemptEligible && (
              <div className="flex items-center justify-between pb-2 mb-1 border-b border-slate-200 dark:border-[#22304A]">
                <div>
                  <span className="text-xs font-bold text-emerald-500">İhracat İstisnası Uygula (KDV %0)</span>
                  <p className="text-[10px] text-slate-400">Bu cari için istisna seçeneği açık — onaylamak için işaretleyin.</p>
                </div>
                <button
                  type="button"
                  data-testid="quote-vat-exempt-toggle"
                  onClick={() => setVatExempt(v => !v)}
                  className={`shrink-0 w-11 h-6 rounded-full transition-colors relative ${vatExempt ? "bg-emerald-500" : "bg-slate-300 dark:bg-slate-600"}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${vatExempt ? "translate-x-5" : ""}`} />
                </button>
              </div>
            )}
            {vatExempt && (
              <textarea
                data-testid="quote-vat-exempt-note"
                rows={2}
                className="zen-input !text-xs mb-2"
                value={vatExemptNoteText}
                onChange={(e) => setVatExemptNoteText(e.target.value)}
              />
            )}
            <div className="flex justify-between text-sm"><span className="text-slate-500">{tr("Ara Toplam")}</span><span className="zen-mono font-bold">{fmt(subtotal, targetCur)}</span></div>
            {/* KDV ORANI — tüm kalemlere uygulanır; seçince manuel tutar sıfırlanır */}
            {!vatExempt && (
              <div className="flex items-center justify-between gap-2">
                <span className="text-slate-500 text-sm">KDV Oranı</span>
                <div className="flex gap-1">
                  {[0, 1, 10, 20].map((r) => {
                    const aktif = items.length > 0 && items.every((it) => Number(it.kdvRate ?? 20) === r);
                    return (
                      <button key={r} type="button" data-testid={`qb-kdv-rate-${r}`}
                        onClick={() => {
                          setItems(items.map((it) => ({ ...it, kdvRate: r })));
                          setQManualKdvStr("");
                          setQManualTotalStr("");
                          toast.success(`KDV oranı %${r} — tüm kalemlere uygulandı`);
                        }}
                        className={`px-2.5 py-1 rounded-lg text-xs font-bold border transition-colors ${aktif ? "bg-[#E5322A] text-white border-[#E5322A]" : "border-slate-300 dark:border-slate-600 hover:border-[#E5322A]/50"}`}>
                        %{r}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {vatExempt ? (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">KDV</span>
                <span className="zen-mono text-emerald-500 font-semibold">%0 (İstisna)</span>
              </div>
            ) : isAdmin ? (
              <div className="flex items-center justify-between text-sm gap-2">
                <span className="text-slate-500 flex items-center gap-1">
                  KDV
                  <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-600 dark:text-amber-400">Manuel</span>
                </span>
                <input
                  data-testid="qb-manual-kdv"
                  className="zen-input !py-0.5 !text-sm w-32 text-right"
                  placeholder={fmt(autoVatAmt, targetCur)}
                  value={qManualKdvStr}
                  onChange={e => setQManualKdvStr(e.target.value)}
                  title="Boş bırakırsanız otomatik hesaplanır"
                />
              </div>
            ) : Object.entries(kdvGroups).sort((a, b) => Number(a[0]) - Number(b[0])).map(([rate, g]) => (
              <div key={rate} className="flex justify-between text-sm">
                <span className="text-slate-500">KDV %{rate} <span className="text-[10px] text-slate-400">(net {fmt(g.net, targetCur)})</span></span>
                <span className="zen-mono">{fmt(g.kdv, targetCur)}</span>
              </div>
            ))}
            <div className="border-t border-slate-200 dark:border-[#22304A] pt-2 mt-2 flex justify-between items-center">
              <span className="text-xs font-bold">{tr("Genel Toplam")}</span>
              {isAdmin ? (
                <input
                  data-testid="qb-manual-total"
                  className="zen-input !py-1 !text-sm font-extrabold w-36 text-right text-[#E5322A]"
                  placeholder={fmt(total, targetCur)}
                  value={qManualTotalStr}
                  onChange={e => setQManualTotalStr(e.target.value)}
                  title="Boş bırakırsanız otomatik hesaplanır"
                />
              ) : (
                <span className="zen-mono text-base font-extrabold text-[#E5322A]" data-testid="quote-grand-total">{fmt(total, targetCur)}</span>
              )}
            </div>
            {/* Diğer 2 para birimi karşılığı — her teklif türünde göster */}
            {rates?.eurToTl > 0 && (() => {
              const eurTl = rates.eurToTl;
              const usdTl = rates.usdToTl || 34;
              const toTL  = targetCur === "TL" ? total : targetCur === "EUR" ? total * eurTl : total * usdTl;
              const toEUR = Math.round(toTL / eurTl);
              const toUSD = Math.round(toTL / usdTl);
              const others = [
                targetCur !== "TL"  && { label: "₺ TL ~", value: Math.round(toTL), cur: "TL" },
                targetCur !== "EUR" && { label: "€ EUR ~", value: toEUR, cur: "EUR" },
                targetCur !== "USD" && { label: "$ USD ~", value: toUSD, cur: "USD" },
              ].filter(Boolean);
              return (
                <div className="mt-1.5 pt-2 border-t border-dashed border-slate-200 dark:border-[#22304A]">
                  <div className="flex justify-between items-center text-[11px] text-slate-400">
                    <span>Diğer para birimleri (KDV'li, ~)</span>
                    <span className="zen-mono" data-testid="quote-total-other-currencies">
                      {others.map(o => fmt(o.value, o.cur)).join("\u00a0·\u00a0")}
                    </span>
                  </div>
                  {targetCur === "TL" && (
                    <div className="text-[10px] text-slate-400 mt-0.5 text-right">
                      1 EUR = {Number(eurTl).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL &nbsp;·&nbsp; 1 USD = {Number(usdTl).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} TL
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}
            </section>
          </main>
        </div>

        {productDrawerOpen && (
          <>
            <div className="fixed inset-0 z-[74] zen-readable-backdrop" data-testid="quote-product-drawer-backdrop" onClick={closeProductDrawer} />
            <aside className="fixed inset-y-0 right-0 z-[75] zen-hc-surface border-l shadow-2xl flex flex-col w-full sm:w-[min(680px,calc(100vw-384px))] max-w-full" data-testid="quote-product-drawer">
              <div className="h-14 shrink-0 px-3 flex items-center justify-between border-b zen-hc-panel" data-testid="quote-product-drawer-header">
                <button type="button" onClick={closeProductDrawer} className="zen-drawer-action rounded-md hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors" data-testid="quote-product-drawer-back-button" aria-label="Geri"><ArrowLeft className="w-5 h-5" /></button>
                {activeGroup && draftItem && (
                  <div className="flex-1 text-center px-3 min-w-0">
                    <div className="text-sm font-bold text-slate-800 dark:text-slate-100 truncate"><PName name={activeGroup.name} /></div>
                  </div>
                )}
                <button type="button" onClick={closeProductDrawer} className="zen-drawer-action rounded-md hover:bg-slate-100 dark:hover:bg-[#22304A] transition-colors" data-testid="quote-product-drawer-close-button" aria-label="Kapat"><X className="w-5 h-5" /></button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto" data-testid="quote-product-drawer-body">
                {activeGroup && draftItem ? (
                  <ProductForm
                    group={activeGroup} item={draftItem} setItem={setDraftItem} assets={assets} productGroups={productGroups} priceItems={priceItems}
                    onSwitchGroup={openProductForm} editingItemIndex={editingItemIndex} onCancel={closeProductDrawer} onAdd={addToCart}
                    computeLineTotal={(g, w, h, q, e) => computeLineTotal(g, w, h, q, e, matrices, rates)} targetCur={targetCur} rates={rates} matrices={matrices}
                    customerDiscountPct={customer?.discountPercent || 0}
                    formFabricGroupsMap={formFabricGroupsMap}
                    hideTopBack
                  />
                ) : (
                  <div className="p-5 space-y-4" data-testid="quote-drawer-product-picker">
                    <div className="relative">
                      <input autoFocus className="zen-input" placeholder="Ürün grubu ara…" value={productQuery}
                        onChange={(e) => setProductQuery(e.target.value)} data-testid="product-type-search" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      {filteredGroups.map((g) => (
                        <button key={g.id} onClick={() => openProductForm(g)} data-testid={`product-type-${g.slug}`}
                          className="p-3 rounded-md border border-slate-200 dark:border-[#22304A] hover:border-[#E5322A]/40 hover:bg-[#E5322A]/5 text-left transition-all group">
                          <div className="text-xs font-semibold group-hover:text-[#E5322A] transition-colors"><PName name={g.name} /></div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </>
        )}
      </div>

      {/* ALT BAR — sadece sağ panel açıkken göster */}
      {rightPanelOpen && (savedQuote ? (
        /* ── Kayıt Sonrası Aksiyon Barı ── */
        <div className="shrink-0 bg-emerald-50 dark:bg-emerald-900/20 border-t-2 border-emerald-500 px-5 py-3 flex flex-wrap justify-between items-center gap-3 z-20">
          <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
            <div>
              <div className="text-sm font-bold">Teklif #{savedQuote.quoteNo} kaydedildi!</div>
              <div className="text-[11px] opacity-75">Müşteriye göndermek için aşağıdaki seçenekleri kullanın</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="quote-saved-continue-edit-btn"
              onClick={() => setSavedQuote(null)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-400 bg-amber-400/10 text-amber-700 dark:text-amber-400 hover:bg-amber-400/20 text-xs font-semibold transition">
              <Pencil className="w-4 h-4" /> Düzenlemeye Devam Et
            </button>
            <button
              data-testid="quote-saved-link-btn"
              onClick={async () => {
                const q = { ...savedQuote, customerInfo: customer, customerPhone: customer?.phone, rates };
                try {
                  toast.loading("Link hazırlanıyor…", { id: "qs-link" });
                  const html = buildTeklifPrintHTML(q, "tr");
                  const barcodeValue = `${String(q.brand || "zen").toUpperCase()}-${q.quoteNo || "X"}-R${String(q.revisionNo || 1).padStart(2, "0")}`;
                  const { data } = await api.post("/share/html-pdf", { html, filename: `Teklif-${q.quoteNo}.pdf`, refType: "quote", refId: q.id, barcodeValue });
                  toast.dismiss("qs-link");
                  const shareLink = shareUrl(data.path);
                  await navigator.clipboard.writeText(shareLink);
                  toast.success("Link Kopyalandı! Dilediğiniz uygulamadan yapıştırabilirsiniz.");
                } catch (e) { toast.dismiss("qs-link"); toast.error("Link oluşturulamadı"); }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-violet-500 bg-violet-500/10 text-violet-600 hover:bg-violet-500/20 text-xs font-semibold transition">
              <Copy className="w-4 h-4" /> Link Kopyala
            </button>
            <button
              data-testid="quote-saved-pdf-btn"
              onClick={async () => {
                const q = { ...savedQuote, customerInfo: customer, customerPhone: customer?.phone, rates };
                const html = buildTeklifPrintHTML(q, "tr");
                try {
                  toast.loading("PDF hazırlanıyor…", { id: "qs-pdf" });
                  const barcodeValue = `${String(q.brand || "zen").toUpperCase()}-${q.quoteNo || "X"}-R${String(q.revisionNo || 1).padStart(2, "0")}`;
                  const { data } = await api.post("/share/html-pdf", { html, filename: `Teklif-${q.quoteNo}.pdf`, refType: "quote", refId: q.id, barcodeValue });
                  toast.dismiss("qs-pdf");
                  window.open(shareUrl(`${data.path}/file`), "_blank");
                } catch (e) { toast.dismiss("qs-pdf"); toast.error("PDF oluşturulamadı"); }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sky-500 bg-sky-500/10 text-sky-600 hover:bg-sky-500/20 text-xs font-semibold transition">
              <FileDown className="w-4 h-4" /> PDF İndir
            </button>
            {/* Tenteks Sipariş Föyü — sadece Tenteks ürünü varsa göster */}
            {savedQuote?.items?.some(it => ["kasetli_tente","mafsalli_tente","giyotin_cam","cam_tavan","surme_cam_sistemi","pergola","bioklimatik","pencere_tentesi"].includes(it.groupSlug)) && (
              <button
                data-testid="quote-saved-tenteks-btn"
                onClick={async () => {
                  // Sauleda kumaş görsellerini base64 olarak önceden yükle
                  const fabricImages = {};
                  await Promise.allSettled(
                    (savedQuote.items || []).map(async it => {
                      const code = it.fabric?.match(/\((\d+)\)$/)?.[1];
                      if (!code) return;
                      try {
                        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/fabrics/image/${code}`);
                        if (!r.ok) return;
                        const blob = await r.blob();
                        await new Promise((res, rej) => {
                          const fr = new FileReader();
                          fr.onloadend = () => { fabricImages[code] = fr.result; res(); };
                          fr.onerror = rej;
                          fr.readAsDataURL(blob);
                        });
                      } catch {}
                    })
                  );
                  const html = buildTenteksSiparisFoyuHTML(savedQuote, { isAdmin, fabricImages });
                  if (html) printViaIframe(html);
                }}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-orange-500 bg-orange-500/10 text-orange-600 hover:bg-orange-500/20 text-xs font-semibold transition">
                <FileText className="w-4 h-4" /> Tenteks Föyü
              </button>
            )}
            <button
              data-testid="quote-saved-preview-btn"
              onClick={() => setShowSavedPreview(true)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-indigo-500 bg-indigo-500/10 text-indigo-600 hover:bg-indigo-500/20 text-xs font-semibold transition">
              <Eye className="w-4 h-4" /> Önizle
            </button>
            {/* Paylaş Menüsü */}
            <div className="relative">
              <button
                data-testid="quote-saved-share-btn"
                onClick={() => setShowShareMenu(v => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-500 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20 text-xs font-semibold transition">
                <Share2 className="w-4 h-4" /> Paylaş
                <ChevronDown className="w-3 h-3 ml-0.5" />
              </button>
              {showShareMenu && (
                <>
                  <div className="fixed inset-0 z-30" onClick={() => setShowShareMenu(false)} />
                  <div className="absolute bottom-full mb-2 right-0 z-40 bg-white dark:bg-[#131B2E] border border-slate-200 dark:border-[#22304A] rounded-xl shadow-2xl overflow-hidden min-w-[230px]">
                    {/* 1) Metni Kopyala */}
                    <button
                      data-testid="quote-share-copy-text"
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-[#1a2540] transition border-b border-slate-100 dark:border-[#22304A]/50"
                      onClick={() => {
                        const q = { ...savedQuote, customerInfo: customer, customerPhone: customer?.phone, rates };
                        const msg = buildQuoteWhatsappText(q, {}, "");
                        navigator.clipboard.writeText(msg);
                        toast.success("Teklif metni kopyalandı");
                        setShowShareMenu(false);
                      }}>
                      <Copy className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-medium">Metni Kopyala</span>
                    </button>
                    {/* 2) PDF Linkini Kopyala */}
                    <button
                      data-testid="quote-share-copy-pdf"
                      disabled={preparingShare}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-[#1a2540] transition border-b border-slate-100 dark:border-[#22304A]/50 disabled:opacity-50"
                      onClick={async () => {
                        setPreparingShare(true);
                        try {
                          const q = { ...savedQuote, customerInfo: customer, customerPhone: customer?.phone, rates };
                          const html = buildTeklifPrintHTML(q, "tr");
                          toast.loading("PDF hazırlanıyor…", { id: "qs-cpdf" });
                          const barcodeValue = `${String(q.brand || "zen").toUpperCase()}-${q.quoteNo || "X"}-R${String(q.revisionNo || 1).padStart(2, "0")}`;
                          const { data } = await api.post("/share/html-pdf", { html, filename: `Teklif-${q.quoteNo}.pdf`, refType: "quote", refId: q.id, barcodeValue });
                          toast.dismiss("qs-cpdf");
                          const link = shareUrl(data.path);
                          navigator.clipboard.writeText(link);
                          toast.success("PDF linki kopyalandı — istediğin yere yapıştır");
                        } catch (e) { toast.dismiss("qs-cpdf"); toast.error("PDF oluşturulamadı"); }
                        finally { setPreparingShare(false); setShowShareMenu(false); }
                      }}>
                      <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                      <span className="font-medium">PDF Linkini Kopyala</span>
                    </button>
                    {/* 3) WhatsApp */}
                    <button
                      data-testid="quote-share-whatsapp"
                      disabled={preparingShare}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-500/10 transition border-b border-slate-100 dark:border-[#22304A]/50 disabled:opacity-50"
                      onClick={async () => {
                        setPreparingShare(true);
                        try {
                          const q = { ...savedQuote, customerInfo: customer, customerPhone: customer?.phone, rates };
                          let link = "";
                          try {
                            const html = buildTeklifPrintHTML(q, "tr");
                            toast.loading("Hazırlanıyor…", { id: "qs-wa2" });
                            const barcodeValue = `${String(q.brand || "zen").toUpperCase()}-${q.quoteNo || "X"}-R${String(q.revisionNo || 1).padStart(2, "0")}`;
                            const { data } = await api.post("/share/html-pdf", { html, filename: `Teklif-${q.quoteNo}.pdf`, refType: "quote", refId: q.id, barcodeValue });
                            link = shareUrl(data.path);
                          } catch (e) { /* PDF olmasa da devam */ }
                          toast.dismiss("qs-wa2");
                          const msg = buildQuoteWhatsappText(q, {}, link);
                          const phone = String(q.customerPhone || "").replace(/[^0-9]/g, "");
                          const intl = phone ? (phone.startsWith("90") ? phone : phone.replace(/^0+/, "90")) : "";
                          const waUrl = intl ? `https://wa.me/${intl}?text=${encodeURIComponent(msg)}` : `https://wa.me?text=${encodeURIComponent(msg)}`;
                          window.open(waUrl, "_blank");
                          logSend({ refType: "quote", refId: q.id, refLabel: String(q.quoteNo), channel: "whatsapp", to: q.customerPhone, subject: `Teklif #${q.quoteNo}` });
                        } catch (e) { toast.error("Hata oluştu"); }
                        finally { setPreparingShare(false); setShowShareMenu(false); }
                      }}>
                      <MessageCircle className="w-4 h-4 shrink-0" />
                      <span className="font-medium">WhatsApp'a Gönder</span>
                    </button>
                    {/* 4) Telegram */}
                    <button
                      data-testid="quote-share-telegram"
                      disabled={preparingShare}
                      className="w-full flex items-center gap-3 px-4 py-3 text-sm text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-500/10 transition disabled:opacity-50"
                      onClick={async () => {
                        setPreparingShare(true);
                        try {
                          const q = { ...savedQuote, customerInfo: customer, customerPhone: customer?.phone, rates };
                          let link = "";
                          try {
                            const html = buildTeklifPrintHTML(q, "tr");
                            toast.loading("Hazırlanıyor…", { id: "qs-tg" });
                            const barcodeValue = `${String(q.brand || "zen").toUpperCase()}-${q.quoteNo || "X"}-R${String(q.revisionNo || 1).padStart(2, "0")}`;
                            const { data } = await api.post("/share/html-pdf", { html, filename: `Teklif-${q.quoteNo}.pdf`, refType: "quote", refId: q.id, barcodeValue });
                            link = shareUrl(data.path);
                          } catch (e) { /* PDF olmasa da devam */ }
                          toast.dismiss("qs-tg");
                          const msg = buildQuoteWhatsappText(q, {}, link);
                          const tgUrl = link
                            ? `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(msg)}`
                            : `https://t.me/share/url?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(msg)}`;
                          window.open(tgUrl, "_blank");
                          logSend({ refType: "quote", refId: q.id, refLabel: String(q.quoteNo), channel: "telegram", to: q.customerPhone, subject: `Teklif #${q.quoteNo}` });
                        } catch (e) { toast.error("Hata oluştu"); }
                        finally { setPreparingShare(false); setShowShareMenu(false); }
                      }}>
                      <Send className="w-4 h-4 shrink-0" />
                      <span className="font-medium">Telegram'a Gönder</span>
                    </button>
                  </div>
                </>
              )}
            </div>
            <button
              data-testid="quote-saved-email-btn"
              disabled={sendingQuoteEmail}
              onClick={async () => {
                const q = { ...savedQuote, customerInfo: customer, customerPhone: customer?.phone, rates };
                if (!customer?.email) { toast.error("Müşterinin e-posta adresi tanımlı değil"); return; }
                setSendingQuoteEmail(true);
                try {
                  const html = buildTeklifPrintHTML(q, "tr");
                  const tpl = await fetchEmailTemplate("quote_email");
                  const { subject, body } = renderEmailTemplate(tpl, {
                    musteri_adi: customer?.name || "", teklif_no: q.quoteNo, toplam: fmt(q.total, q.currency),
                    para_birimi: q.currency, firma_adi: "ZEN Mekanizmalı Perde Sistemleri",
                  });
                  await api.post("/email-settings/send-pdf", {
                    to: customer.email, subject, html, bodyText: body,
                    filename: `Teklif-${q.quoteNo}.pdf`, refType: "quote", refId: q.id, refLabel: String(q.quoteNo),
                  });
                  toast.success(`E-posta gönderildi — ${customer.email}`);
                } catch (e) { toast.error(apiErrorMessage(e, "E-posta gönderilemedi")); }
                finally { setSendingQuoteEmail(false); }
              }}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sky-500 bg-sky-500/10 text-sky-600 hover:bg-sky-500/20 text-xs font-semibold transition disabled:opacity-50">
              <Mail className="w-4 h-4" /> {sendingQuoteEmail ? "Gönderiliyor..." : "E-posta ile Gönder"}
            </button>
            <button
              data-testid="quote-saved-close-btn"
              onClick={() => onSaved(savedQuote)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-200 dark:bg-[#22304A] hover:bg-slate-300 dark:hover:bg-[#2a3a5a] text-slate-700 dark:text-slate-300 text-xs font-semibold transition">
              <X className="w-4 h-4" /> Kapat
            </button>
          </div>
        </div>
      ) : (
        /* ── Normal Alt Bar ── */
        <div className="shrink-0 bg-[#E4EAF3] dark:bg-[#131B2E] border-t border-slate-300 dark:border-[#22304A] px-5 py-3 flex flex-wrap justify-between items-center gap-2 z-20">
          {/* Sol: Banka seçici */}
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {bankAccounts.length > 0 && (
              <div className="flex items-center gap-1.5 min-w-0">
                <Building2 className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <select value={selectedBankId} onChange={e => setSelectedBankId(e.target.value)}
                  className="zen-input !py-1 !text-xs max-w-[220px]" data-testid="quote-bank-select">
                  <option value="">— Banka bilgisi seçin —</option>
                  {bankAccounts.map(b => (
                    <option key={b.id} value={b.id}>{`${b.bankName} — ${b.currency}${b.isDefault ? " (Varsayılan)" : ""}`}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          {/* Sağ: Önizle + Kaydet */}
          <div className="flex items-center gap-2">
            <button onClick={() => setRightPanelOpen(false)} className="zen-btn-ghost" data-testid="quote-builder-cancel-btn">{tr("Kapat")}</button>
            <button onClick={() => {
              if (!customerId) { toast.error("Müşteri seçin"); return; }
              if (items.length === 0) { toast.error("Ürün ekleyin"); return; }
              setShowPreview(true);
            }} className="zen-btn-ghost flex items-center gap-1.5" data-testid="quote-preview-btn">
              <Eye className="w-4 h-4" /> Önizle
            </button>
            <button onClick={saveQuote} disabled={saving || !dataReady} className="zen-btn-primary disabled:opacity-50" data-testid="quote-save-btn">
              <FileSignature className="w-4 h-4" /> {!dataReady ? "Yükleniyor…" : saving ? "Kaydediliyor…" : isRevMode ? "Revize Et & Kaydet" : isEdit ? (saveAsOrder ? "Siparişi Güncelle" : "Teklifi Güncelle") : (saveAsOrder ? "Siparişi Kaydet" : "Teklifi Kaydet")}
            </button>
          </div>
        </div>
      ))}
      

      {showPreview && (
        <QuotePrintModal
          quote={{
            quoteNo: editingQuote?.quoteNo || "ÖNİZLEME",
            customerId,
            customerName: customer?.name || "",
            customerInfo: customer,
            customerPhone: customer?.phone || "",
            projectName,
            items,
            subtotal,
            total,
            vatBreakdown: Object.entries(kdvGroups).map(([rate, g]) => ({ rate: Number(rate), net: g.net, kdv: g.kdv })),
            vatExempt, vatExemptNote: vatExempt ? vatExemptNoteText : "",
            currency: targetCur,
            notes,
            brand,
            createdAt: new Date().toISOString(),
            bankAccountId: selectedBankId,
            validDays: Number(validDays) || 15,
            rates,
          }}
          onClose={() => setShowPreview(false)}
        />
      )}

      {showSavedPreview && savedQuote && (
        <QuotePrintModal quote={savedQuote} onClose={() => setShowSavedPreview(false)} />
      )}

      {showManualRow && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="zen-panel w-full max-w-sm p-5 space-y-3">
            <h4 className="text-sm font-bold">Manuel Satır Ekle</h4>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Ürün / Açıklama</label>
              <input className="zen-input" data-testid="manual-row-product" placeholder="Örn: Özel Aksesuar, Ek Hizmet…" value={manualRow.product} onChange={(e) => setManualRow(p => ({ ...p, product: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Birim</label>
                <select className="zen-input" data-testid="manual-row-unit" value={manualRow.unit} onChange={(e) => setManualRow(p => ({ ...p, unit: e.target.value }))}>
                  <option value="adet">Adet</option>
                  <option value="m²">m²</option>
                  <option value="mt">Metre</option>
                  <option value="takım">Takım</option>
                  <option value="set">Set</option>
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Miktar</label>
                <input type="number" min="1" step="1" className="zen-input" data-testid="manual-row-qty" value={manualRow.qty} onChange={(e) => setManualRow(p => ({ ...p, qty: e.target.value }))} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Birim Fiyat</label>
                <input type="number" min="0" step="0.01" className="zen-input" data-testid="manual-row-price" value={manualRow.unitPrice} onChange={(e) => setManualRow(p => ({ ...p, unitPrice: e.target.value }))} />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">Para Birimi</label>
                <select className="zen-input" data-testid="manual-row-currency" value={manualRow.currency} onChange={(e) => setManualRow(p => ({ ...p, currency: e.target.value }))}>
                  <option value="TL">TL</option>
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1">KDV Oranı</label>
              <select className="zen-input" data-testid="manual-row-kdv" value={manualRow.kdvRate} onChange={(e) => setManualRow(p => ({ ...p, kdvRate: e.target.value }))}>
                <option value={0}>%0</option>
                <option value={10}>%10</option>
                <option value={20}>%20</option>
              </select>
            </div>
            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowManualRow(false)} className="zen-btn-ghost flex-1">Vazgeç</button>
              <button onClick={addManualRow} className="zen-btn-primary flex-1" data-testid="manual-row-confirm-btn">Ekle</button>
            </div>
          </div>
        </div>
      )}

      {showMontajPrompt && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="zen-panel w-full max-w-sm p-5">
            <h4 className="text-sm font-bold mb-3">Montaj Bedeli Ekle</h4>
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Açıklama</label>
            <input className="zen-input mb-3" data-testid="montaj-desc-input" value={montajDesc} onChange={(e) => setMontajDesc(e.target.value)} />
            <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Tutar ({targetCur})</label>
            <input type="number" className="zen-input mb-4" data-testid="montaj-amount-input" value={montajAmount} onChange={(e) => setMontajAmount(e.target.value)} />
            <div className="flex gap-2">
              <button onClick={() => setShowMontajPrompt(false)} className="zen-btn-ghost flex-1">Vazgeç</button>
              <button onClick={addMontajFee} className="zen-btn-primary flex-1" data-testid="montaj-confirm-btn">Ekle</button>
            </div>
          </div>
        </div>
      )}

      {showDikisPrompt && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="zen-panel w-full max-w-sm p-5 space-y-3">
            <h4 className="text-sm font-bold">Dikiş Bedeli Ekle</h4>
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Dikiş Türü</label>
              <select
                className="zen-input w-full"
                data-testid="dikis-type-select"
                value={dikisRow.costId}
                onChange={e => {
                  const sc = sewingCosts.find(c => c.id === e.target.value);
                  if (sc) setDikisRow(r => ({ ...r, costId: sc.id, productType: sc.productType, unitPrice: sc.unitPrice, unit: sc.unit }));
                  else setDikisRow(r => ({ ...r, costId: e.target.value }));
                }}
              >
                <option value="">— Tür seçin —</option>
                {sewingCosts.map(sc => (
                  <option key={sc.id} value={sc.id}>
                    {sc.code}. {sc.productType} ({sc.unitPrice.toLocaleString("tr-TR")} ₺/{sc.unit})
                  </option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Miktar ({dikisRow.unit || "MT"})</label>
                <input
                  type="number" min="0.5" step="0.5" className="zen-input"
                  data-testid="dikis-qty-input"
                  value={dikisRow.qty}
                  onChange={e => setDikisRow(r => ({ ...r, qty: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Birim Fiyat (₺)</label>
                <input
                  type="number" min="0" step="1" className="zen-input"
                  data-testid="dikis-price-input"
                  value={dikisRow.unitPrice}
                  onChange={e => setDikisRow(r => ({ ...r, unitPrice: e.target.value }))}
                />
              </div>
            </div>
            {dikisRow.productType && (
              <div className="text-xs text-slate-500 bg-slate-50 dark:bg-slate-800 rounded-lg px-3 py-2">
                Toplam: <span className="font-bold text-amber-600">
                  {(Number(dikisRow.unitPrice) * Number(dikisRow.qty)).toLocaleString("tr-TR")} ₺
                </span> ({dikisRow.productType})
              </div>
            )}
            <div className="flex gap-2 pt-1">
              <button onClick={() => setShowDikisPrompt(false)} className="zen-btn-ghost flex-1">Vazgeç</button>
              <button onClick={addDikisFee} className="zen-btn-primary flex-1" data-testid="dikis-confirm-btn">Sepete Ekle</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ayrı dosyalara taşınan bileşenler (geriye dönük uyumluluk için re-export) ─
export { QuoteViewsModal } from "../components/QuoteViewsModal";
export { QuoteRevisionHistoryModal } from "../components/QuoteRevisionHistoryModal";
